/**
 * Product Wizard
 * Hillsdale UI release 2026-10-13: refresh the dependent preview asset revision.
 * Generic step-by-step variant selection for all personalized products.
 * Reads product options dynamically — no hardcoded product types.
 */

// Pet-count → per-count watercolor portrait product. The four products share
// one flow (graduated from exp_preview_multi_pet): price display and add-to-cart
// remap to the product matching the number of FILLED pet slots. Numeric ids
// let analytics stamp the mapped product before any sibling fetch resolves.
// `set` groups products that may be swapped between. The popup event sells the
// same portraits printed on site, as separate booth-only products stocked at
// the event location, and those must only ever remap to each other: remapping a
// booth shopper onto an 'online' sibling would silently move them onto a
// SHIPPING product mid-purchase, and they would discover it at checkout with
// pickup no longer offered. Resolution by petCount alone cannot express that,
// so every count lookup goes through petSiblings().
const MULTI_PET_PRODUCTS = [
  { set: 'online', petCount: 1, handle: 'custom-one-pet-portrait', id: 15401062465833 },
  { set: 'online', petCount: 2, handle: 'custom-two-pet-portrait-1', id: 15401062498601 },
  { set: 'online', petCount: 3, handle: 'custom-three-pet-portrait', id: 15401062531369 },
  { set: 'online', petCount: 4, handle: 'custom-four-pet-portrait-1', id: 15401062564137 },
  // Booth copies, stocked only at the event location so checkout offers pickup
  // and nothing else. Only the 1-pet product is on the booth menu; 2-4 are
  // reached solely by remapping, which is why they are in no collection.
  { set: 'booth', petCount: 1, handle: 'custom-watercolor-one-pet-portrait-booth', id: 15401062957353 },
  // Landscape from 2 pets up, matching online: one pet reads better upright, and
  // two or more sit side by side. The print station gained a 10x8 spec, so these
  // are printable at the booth.
  { set: 'booth', petCount: 2, handle: 'custom-watercolor-two-pet-portrait-booth', id: 15401063186729 },
  { set: 'booth', petCount: 3, handle: 'custom-watercolor-three-pet-portrait-booth', id: 15401063219497 },
  { set: 'booth', petCount: 4, handle: 'custom-watercolor-four-pet-portrait-booth', id: 15401063317801 },
];

/** The per-count products a given product is allowed to remap to — its own set,
 *  or none if it is not a per-count product at all. */
function petSiblings(productId) {
  const own = MULTI_PET_PRODUCTS.find(p => p.id === productId);
  return own ? MULTI_PET_PRODUCTS.filter(p => p.set === own.set) : [];
}

// Painted-door interest test (exp_multi_pet_interest, backend issue #77):
// single-photo products OUTSIDE the multi-pet set show a working "Add
// Another Pet" button whose click opens an honest coming-soon dialog.
// Memorial products are excluded — sensitive context, owner's call.
const MULTI_PET_INTEREST_EXCLUDED = new Set([
  7724448809065, // custom-memorial-pet-portrait
  7724454674537, // custom-memorial-pet-portrait-1 (rainbow)
  7724442222697, // custom-memorial-pet-portrait-with-halo
  7725732790377, // custom-pet-memorial-mug
  7725788889193, // memorial-custom-pet-blanket-with-halo-1
  7725789118569, // memorial-custom-pet-blanket-with-rainbow
]);

// Returned by resolveMappedForCart() when a remap IS required but could not be
// resolved. Distinct from null, which means "no remap applies here" — the two
// were indistinguishable before, so every caller treated a failed remap as
// "no remap needed" and shipped the page product instead. See the reversal
// note on resolveMappedForCart().
const REMAP_UNRESOLVED = Symbol('remap-unresolved');

// How far the in-page button row may slide under the sticky header, while
// scrolling past it, before the fixed bar takes over (measureNav). Larger
// hides the in-page Add to Cart for longer; smaller leaves a taller blank
// band where the nav was. 12px: a band under 40px, the button hidden for 12px.
const NAV_ATTACH_UNDER_HEADER = 12;

// Instant Preview requirement test (frontend#239). In the test arm, Add to
// Cart needs a photo, the required fields and an approved preview; control is
// today's flow. The arm comes from snippets/experiments.liquid and is read at
// the moment it matters, with === 'test', so a shopper holding no arm (flag
// absent, flags not loaded, a 0% kill) is control.
const PREVIEW_REQUIRED_FLAG = 'exp_preview_required';

class ProductWizard extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
    this.selectedOptions = {};
    // Per-slot upload state. Keyed by field label (e.g. "Pet Photo", "Pet Photo 2").
    // After upload completes: { upload_id, photo_url, photo_path, pet_name, fileName, uploading: false, selectSeq }
    // While in flight:        { uploading: true, selectSeq, fileName, _previewUrl, _abort }
    // After tile selection:   { upload_id, photo_url, photo_path, pet_name, uploading: false, selectSeq }
    /** @type {Record<string, any>} */
    this.uploads = {};
    this.priorUploads = []; // fetched once, shared across all slots
    this.currentGalleryIndex = 0;
    this._galleryToken = 0; // guards against out-of-order gallery swaps on rapid clicks
    // Multi-pet state — see setupMultiPet().
    this._siblingProducts = {}; // handle → in-flight fetch Promise for per-count sibling data
    this._siblingResolved = {}; // handle → resolved sibling product JSON (sync access for analytics)
    this._mappedPriceToken = 0; // discards stale async mapped-price overlays
    this._addPetLabelToken = 0; // discards stale async add-button price hints
  }

  connectedCallback() {
    const dataEl = this.querySelector('[data-product-json]');
    if (!dataEl) return;
    this.productData = JSON.parse(dataEl.textContent);

    // Cache DOM refs
    this.steps = this.querySelectorAll('[data-step]');
    this.totalSteps = this.productData.totalSteps;
    this.backBtn = this.querySelector('[data-back]');
    this.nextBtn = this.querySelector('[data-next]');
    this.addToCartBtn = this.querySelector('[data-add-to-cart]');
    // Two copies: one under the buttons in the page, one in the fixed bar.
    this.quantityHints = this.querySelectorAll('[data-quantity-hint]');
    this.priceDisplay = this.querySelector('[data-price]');
    this.comparePriceDisplay = this.querySelector('[data-compare-price]');
    this.subtotalDisplays = this.querySelectorAll('[data-subtotal]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.stepIndicators = this.querySelectorAll('[data-step-indicator]');
    this.navEl = this.querySelector('[data-nav]');

    // Gallery refs
    this.galleryMainImg = this.querySelector('[data-gallery-main-img]');
    this.galleryPrevBtn = this.querySelector('[data-gallery-prev]');
    this.galleryNextBtn = this.querySelector('[data-gallery-next]');
    this.galleryThumbs = this.querySelectorAll('[data-thumb-index]');
    this.mediaList = this.productData.media || [];

    // Option selection listeners
    this.querySelectorAll('.wizard-swatch__input').forEach(input => {
      input.addEventListener('change', (e) => this.handleOptionChange(e));
    });

    // Navigation
    this.backBtn?.addEventListener('click', () => this.prevStep());
    this.nextBtn?.addEventListener('click', () => this.nextStep());
    this.addToCartBtn?.addEventListener('click', () => this.addToCart());

    // Gallery
    this.setupGallery();

    // If the shopper arrived from a personalized P2A upsell card, show that
    // exact composite image in the gallery instead of the default.
    this.applyP2APreviewImage();

    // Frame toggle + tooltips
    this.setupFrameTooltips();

    // Photo uploads (supports multiple upload zones)
    this.setupPhotoUploads();

    // Multi-pet slot reveal/remove + per-count product mapping. Must run
    // before initializeDefaults() so the first updatePrice() already knows
    // whether the test arm is active.
    this.setupMultiPet();

    // Painted-door interest button on products WITHOUT multi-pet support.
    // Runs after setupMultiPet so it can read the latched multiPetActive.
    this.setupMultiPetInterest();

    // Photo guide modal
    this.setupPhotoGuide();

    // "Send photo later" one-time reminder modal
    this.setupDeferModal();

    // Sticky nav observer
    this.setupStickyNav();

    // Pet name inputs — track customization_started on first keystroke
    this.querySelectorAll('[data-line-item-property]').forEach(input => {
      input.addEventListener('input', () => this.trackCustomizationStarted(), { once: true });
    });

    // Initialize
    this.initializeDefaults();
    this.updateUI();

    // Fire view_item on every product page load. Uses the default/initially-
    // selected variant; if the user later changes a swatch we fire a separate
    // variant_selected event rather than re-firing view_item (matches GA4
    // semantics).
    this.trackViewItem();

    // Instant Preview (if enabled for this product). On the 2-4-pet products
    // the metafield arrives while the multi-pet popup is still arm-gated —
    // the legacy popup would preview slot 1 only on a multi-pet purchase, so
    // the control arm keeps those pages preview-less (today's behavior) and
    // only the test arm initializes.
    const ownPetCount = MULTI_PET_PRODUCTS.find(p => p.id === this.productData.id)?.petCount || 1;
    if (this.productData.previewEnabled) {
      if (ownPetCount > 1 && !this.multiPetActive) {
        const previewSection = this.querySelector('[data-preview-section]');
        if (previewSection) {
          previewSection.hidden = true;
        }
      } else {
        this.initInstantPreview();
      }
    }

    // The requirement test's arm usually lands after first paint (flags load
    // asynchronously). When it does, repaint Add to Cart for it and count the
    // exposure. posthog is the snippet's queueing stub until the library
    // loads, so this is safe to call now. The loaded library hands back an
    // unsubscribe (kept for disconnectedCallback); the stub cannot, so the
    // callback also stands down once this wizard has left the page.
    try {
      const unsubscribe = window.posthog?.onFeatureFlags?.(() => {
        if (!this.isConnected) {
          return;
        }
        this._flagsSettled = true;
        this.updateBoothApprovalState();
        this.firePreviewRequirementExposure();
      });
      this._flagsUnsubscribe = typeof unsubscribe === 'function' ? unsubscribe : null;
    } catch (_) {}
    // Flags that never answer (blocked, offline) still end in an exposure,
    // as "unassigned", after a grace period rather than never.
    setTimeout(() => {
      if (!this.isConnected) {
        return;
      }
      this._flagsSettled = true;
      this.firePreviewRequirementExposure();
    }, /* flagsGraceMs */ 10000);
  }

  initializeDefaults() {
    const cv = this.productData.currentVariant;
    if (!cv) return;

    this.productData.options.forEach((option, index) => {
      const value = cv.options[index];
      if (value) {
        this.selectedOptions[option.name] = value;
        const radio = this.querySelector(
          `input[name="${CSS.escape(option.name)}"][value="${CSS.escape(value)}"]`
        );
        if (radio) radio.checked = true;
      }
    });

    this.updatePrice();
  }

  /* ── Option Selection ── */

  handleOptionChange(e) {
    const input = e.target;
    this.selectedOptions[input.name] = input.value;
    this.updatePrice();
    this.scrollGalleryToVariant();
    this.trackCustomizationStarted();
    this.reviewApprovalAfterOptionChange(input.name);
    // variant_selected (committed-choice) fires on NEXT — see nextStep().
    // We ALSO fire variant_swatch_clicked here for per-click funnel
    // analysis (e.g. "user clicked 3 colors then bailed"). Distinct event
    // name keeps the two non-overlapping in PostHog.
    this.trackUploadEvent('variant_swatch_clicked', {
      product_id: this.productData.id,
      option_name: input.name,
      option_value: input.value,
      step: this.currentStep,
    });
  }

  /**
   * The shopper changed an option after approving a design. Decide whether
   * the approval still describes what the press would now print.
   *
   * Booth products and the Instant Preview requirement test arm drop it, and
   * Add to Cart waits for a new "Looks Good". Everywhere else the approval is
   * deliberately kept. Clearing `_preview_id` there would not protect
   * anything: resolvePreviewForLineItem (printify-fulfillment.server.ts) falls
   * back to the newest product_previews row for (user, product, upload) with no
   * variant filter, so the same row is found either way — all it would cost is
   * the explicit link, on the path where a human reviews the art anyway. It is
   * only marked outdated, so the line does not claim an approval it no longer
   * has (_preview_approved, previewApprovalValid).
   *
   * @param {string} optionName - the option group that changed, for telemetry
   */
  reviewApprovalAfterOptionChange(optionName) {
    if (!this.previewApproved()) return;
    if (!this.printSignatureChanged()) {
      // Back on a selection that prints the approved file (A to B and back to
      // A), so an earlier change no longer makes the approval outdated.
      this._approvalOutdated = false;
      return;
    }
    if (!this.boothApprovalRequired() && !this.previewRequired()) {
      this._approvalOutdated = true;
      return;
    }

    // Distinguishes "you haven't previewed yet" from "your approval just
    // lapsed" — only the second one deserves an explanation on screen.
    this._boothApprovalStale = true;
    // Booth dashboards read booth_approval_invalidated; the online test arm
    // reports under its own name so the two never mix.
    const booth = this.boothApprovalRequired();
    this.trackUploadEvent(booth ? 'booth_approval_invalidated' : 'preview_approval_invalidated', {
      product_id: this.productData.id,
      option_name: optionName,
      option_value: this.selectedOptions[optionName],
      // false = we could not read a signature (no map, unknown key, active
      // multi-pet remap) and invalidated on the safe default rather than on a
      // proven difference. Worth watching: if it is mostly false, the map is
      // not reaching the page and every shopper is re-approving needlessly.
      signature_known: !!this.currentPrintSignature(),
      // Which gate asked for the re-approval: the booth's, or the Instant
      // Preview requirement test's (frontend#239).
      gate: booth ? 'booth' : 'preview_required',
    });
    // Drops previewId/previewImageUrl/approvedPrintSignature and repaints the
    // button through updateBoothApprovalState.
    this.revertGalleryIfNeeded();
  }

  /**
   * The shopper edited typed text. If the pet name or custom text the approved
   * design shows is now different, the approval no longer describes what the
   * order line will carry, so it lapses as it does for a new photo, and on
   * every product: previewApproved() has to mean "approved what this line
   * carries" wherever it is read, not only at the booth gate.
   *
   * Compared against the text stamped at "Looks Good" rather than cleared on
   * any input event: updatePreviewButtonState dispatches a synthetic one with
   * the text unchanged, and that must not cost anyone their approval. The same
   * compare lets a paste of the identical text, or stray spaces (trimmed, as
   * the render trims them), keep it.
   *
   * Per-count multi-pet pages keep their own per-slot rename listeners
   * (setupMultiPet), so this stands down there rather than act twice.
   */
  reviewApprovalAfterTextEdit() {
    if (this.multiPetActive || !this.previewApproved()) {
      return;
    }
    if (this.instantPreview?.approvalTextKey() === this.approvedPreviewText) {
      return;
    }
    // Also repaints a booth product's Add to Cart as blocked.
    this.revertGalleryIfNeeded();
    // Show that the preview needs approving again: nudgeToPreview's pulse,
    // without its scroll, which would move the page while they type. The
    // class is dropped and re-added so a second lapse animates again.
    const btn = this.querySelector('[data-preview-btn]');
    if (btn) {
      btn.classList.remove('wizard-preview__btn--pulse');
      void btn.offsetWidth;
      btn.classList.add('wizard-preview__btn--pulse');
    }
  }

  findMatchingVariant() {
    return this.productData.variants.find(variant => {
      return this.productData.options.every((option, index) => {
        const selectedValue = this.selectedOptions[option.name];
        return !selectedValue || variant.options[index] === selectedValue;
      });
    });
  }

  updatePrice() {
    const variant = this.findMatchingVariant();
    if (!variant) return;

    this.renderVariantPrice(variant);

    if (this.multiPetActive) {
      // Async overlay: with 2+ slots revealed the shopper pays for the
      // matching per-count product, so its variant price replaces the page
      // product's once the (cached) sibling data resolves.
      this.applyMappedPrice();
      this.updateAddPetButton();
    }
  }

  renderVariantPrice(variant) {
    const price = this.formatMoney(variant.price);
    if (this.priceDisplay) this.priceDisplay.textContent = price;
    this.subtotalDisplays.forEach(el => { el.textContent = price; });
    if (this.variantInput) this.variantInput.value = variant.id;

    if (this.comparePriceDisplay) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceDisplay.textContent = this.formatMoney(variant.compare_at_price);
        this.comparePriceDisplay.hidden = false;
      } else {
        this.comparePriceDisplay.hidden = true;
      }
    }

    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !variant.available;
      this.addToCartBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
      // Read by the "· $X" suffix product-wizard.css draws after the label.
      // An attribute rather than a child element, because submitCart saves and
      // restores the label with textContent, which would flatten a child.
      this.addToCartBtn.dataset.price = price;
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ── Step Navigation ── */

  nextStep() {
    if (!this.validateCurrentStep()) return;

    // Fire variant_selected for each option group on the step the user is
    // confirming (i.e. the current step, before we increment). One event
    // per option name. This captures the committed choice rather than
    // mid-comparison swatch clicks. The user accepting the default value
    // without changing anything still counts as confirmation.
    this.trackVariantSelectedForCurrentStep();

    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.updateUI();
      this.scrollDetailsToTop();
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateUI();
      this.scrollDetailsToTop();
    }
  }

  validateCurrentStep() {
    const currentStepEl = this.querySelector(`[data-step="${this.currentStep}"]`);
    if (!currentStepEl) return true;

    // Check radio groups (variant options)
    const radioGroups = new Set();
    currentStepEl.querySelectorAll('.wizard-swatch__input').forEach(input => {
      radioGroups.add(input.name);
    });

    for (const groupName of radioGroups) {
      if (groupName.startsWith('_')) continue; // skip internal toggles
      if (!this.selectedOptions[groupName]) {
        const firstSwatch = currentStepEl.querySelector(`input[name="${CSS.escape(groupName)}"]`);
        if (firstSwatch) {
          const container = firstSwatch.closest('.wizard-swatches, .wizard-frame-choices');
          container?.classList.add('wizard-swatches--error');
          setTimeout(() => container?.classList.remove('wizard-swatches--error'), 2000);
        }
        return false;
      }
    }

    // Check required text fields on personalization step — but skip ones whose
    // slot is hidden (pet-name fields for slots without an upload yet, and
    // unrevealed multi-pet groups where the hidden attribute sits on the group
    // wrapper rather than the input's own .wizard-field).
    const requiredInputs = currentStepEl.querySelectorAll('[data-line-item-property][required]');
    for (const input of requiredInputs) {
      if (this.isSlotHidden(input)) continue;
      if (!input.value.trim()) {
        input.focus();
        input.classList.add('wizard-field__input--error');
        setTimeout(() => input.classList.remove('wizard-field__input--error'), 2000);
        return false;
      }
    }

    // Check required photo uploads — unrevealed multi-pet groups aren't required
    const uploadZones = currentStepEl.querySelectorAll('[data-upload]');
    for (const zone of uploadZones) {
      if (this.isSlotHidden(zone)) continue;
      const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';
      if (!this.uploads[fieldLabel]) {
        this.showUploadError(zone, 'Please upload a photo to continue.');
        return false;
      }
    }

    return true;
  }

  updateUI() {
    this.steps.forEach(step => {
      const stepNum = parseInt(step.dataset.step);
      if (stepNum === this.currentStep) {
        step.hidden = false;
        step.classList.add('wizard-step--entering');
        requestAnimationFrame(() => {
          step.classList.remove('wizard-step--entering');
          step.classList.add('wizard-step--active');
        });
      } else {
        step.classList.remove('wizard-step--active');
        step.hidden = true;
      }
    });

    this.stepIndicators.forEach(indicator => {
      const stepNum = parseInt(indicator.dataset.stepIndicator);
      indicator.classList.toggle('active', stepNum === this.currentStep);
      indicator.classList.toggle('completed', stepNum < this.currentStep);
    });

    if (this.backBtn) this.backBtn.hidden = this.currentStep === 1;

    const isLastStep = this.currentStep === this.totalSteps;
    if (this.nextBtn) this.nextBtn.hidden = isLastStep;
    if (this.addToCartBtn) this.addToCartBtn.hidden = !isLastStep;
    // Reads "change the quantity in your cart", so it is only true advice once
    // there is something to add. Tied to isLastStep rather than given its own
    // condition: two conditions that must agree are two conditions that can
    // drift, and the failure would be a standing line over an untouched form.
    this.quantityHints.forEach((hint) => { hint.hidden = !isLastStep; });
    // The Instant Preview button lives in the nav now (so it can ride along
    // into the fixed bar) and is only meant for the personalize step.
    const stepEl = this.querySelector(`[data-step="${this.currentStep}"]`);
    this.navEl?.classList.toggle('wizard__nav--personalize', !!stepEl?.hasAttribute('data-step-personalize'));
    // The button is only now becoming visible, so it has to arrive already
    // wearing the right state rather than looking live until the first tap.
    this.updateBoothApprovalState();
    // The in-page nav just changed shape, and while it is the fixed bar no
    // resize of it would say so.
    this.measureNav();
    // Counted when the Instant Preview button comes on screen, which is the
    // personalize step (frontend#239).
    this.firePreviewRequirementExposure();

    this.scrollProgressToActiveStep();
  }

  scrollProgressToActiveStep() {
    const progressBar = this.querySelector('.wizard__progress');
    const activeIndicator = this.querySelector(`[data-step-indicator="${this.currentStep}"]`);
    if (!progressBar || !activeIndicator) return;

    // Use getBoundingClientRect for accurate position relative to the scroll container
    requestAnimationFrame(() => {
      const barRect = progressBar.getBoundingClientRect();
      const stepRect = activeIndicator.getBoundingClientRect();
      const relativeLeft = stepRect.left - barRect.left + progressBar.scrollLeft;
      const scrollLeft = Math.max(0, relativeLeft - (barRect.width / 2) + (stepRect.width / 2));
      progressBar.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    });
  }

  scrollDetailsToTop() {
    requestAnimationFrame(() => {
      const title = this.querySelector('.wizard__title');
      if (title) {
        title.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ── Image Gallery ── */

  setupGallery() {
    if (!this.galleryMainImg || !this.mediaList.length) return;

    this.galleryPrevBtn?.addEventListener('click', () => this.navigateGallery(-1));
    this.galleryNextBtn?.addEventListener('click', () => this.navigateGallery(1));

    this.galleryThumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.thumbIndex);
        this.setGalleryIndex(index);
      });
    });

    this.updateGalleryArrows();
  }

  navigateGallery(direction) {
    const newIndex = this.currentGalleryIndex + direction;
    if (newIndex >= 0 && newIndex < this.mediaList.length) {
      this.setGalleryIndex(newIndex);
    }
  }

  setGalleryIndex(index) {
    if (index < 0 || index >= this.mediaList.length) return;
    this.currentGalleryIndex = index;

    const media = this.mediaList[index];
    if (this.galleryMainImg) {
      const img = this.galleryMainImg;
      // Each navigation gets a token; if a newer one starts before this one
      // finishes loading, the stale swap is discarded (prevents flashing to the
      // wrong image on rapid prev/next clicks).
      const token = ++this._galleryToken;

      if (window.InstaMePublicImageCache) {
        // Keep the current image visible while the next cached image decodes.
        // No fixed animation delay on the shared iPad.
        window.InstaMePublicImageCache.resolve(media.src).then(async src => {
          const ready = new Image();
          ready.src = src;
          try { await ready.decode(); } catch { return; }
          if (token !== this._galleryToken) return;
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
          img.src = src;
          img.alt = media.alt;
          img.style.opacity = '1';
        });
      } else {

      // Start fading out the current image (CSS transition is opacity 0.2s).
      img.style.opacity = '0';

      // Preload + decode the new image and wait for the fade-out in parallel.
      // We only swap the src and fade back in once BOTH are done — otherwise the
      // old image (still on screen until the new src decodes) fades back in, then
      // flashes to the new image once it finally loads.
      const preload = new Image();
      preload.src = media.src;
      const decoded = preload.decode().catch(() => {}); // resolve even on decode error
      const fadedOut = new Promise((r) => setTimeout(r, 200)); // matches CSS transition

      Promise.all([decoded, fadedOut]).then(() => {
        if (token !== this._galleryToken) return; // superseded by a newer navigation
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = media.src;
        img.alt = media.alt;
        img.style.opacity = '1';
      });
      }
    }

    this.galleryThumbs.forEach((thumb, i) => {
      thumb.classList.toggle('wizard-gallery__thumb--active', i === index);
    });

    const activeThumb = this.galleryThumbs[index];
    if (activeThumb && activeThumb.parentElement) {
      const container = activeThumb.parentElement;
      const thumbLeft = activeThumb.offsetLeft;
      const thumbWidth = activeThumb.offsetWidth;
      const containerWidth = container.offsetWidth;
      container.scrollTo({
        left: thumbLeft - (containerWidth / 2) + (thumbWidth / 2),
        behavior: 'smooth'
      });
    }

    this.updateGalleryArrows();
  }

  updateGalleryArrows() {
    if (this.galleryPrevBtn) {
      this.galleryPrevBtn.disabled = this.currentGalleryIndex === 0;
    }
    if (this.galleryNextBtn) {
      this.galleryNextBtn.disabled = this.currentGalleryIndex >= this.mediaList.length - 1;
    }
  }

  /**
   * Show the personalized composite the shopper saw on the P2A upsell card.
   * photo-to-art.js stashes the card's data: URL (one-shot, in sessionStorage)
   * only when the clicked card was personalized — plain cards store nothing, so
   * those product pages keep their default image. We consume (remove) the marker
   * and guard on the product handle so a stale marker can't land on the wrong page.
   */
  applyP2APreviewImage() {
    let payload;
    try {
      const raw = sessionStorage.getItem('instame_p2a_card_image');
      if (!raw) return;
      sessionStorage.removeItem('instame_p2a_card_image'); // one-shot
      payload = JSON.parse(raw);
    } catch (_) {
      return;
    }
    if (!payload || !payload.src) return;

    // Only apply to the product the card was for.
    const currentHandle = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (payload.handle && currentHandle && payload.handle !== currentHandle) return;

    this.insertPreviewIntoGallery(payload.src, { prepend: true });
  }

  /**
   * @param {string} imageUrl
   * @param {{ prepend?: boolean }} [options] - prepend:true inserts the preview
   *   as the first gallery image (used by the P2A upsell flow). Default appends.
   */
  insertPreviewIntoGallery(imageUrl, options = {}) {
    if (!this.galleryMainImg) return;
    const { prepend = false } = options;

    if (this._previewMediaIndex != null) {
      this.mediaList[this._previewMediaIndex].src = imageUrl;
      const existingThumb = this.querySelector(`[data-thumb-index="${this._previewMediaIndex}"]`);
      const existingThumbImg = existingThumb?.querySelector('.wizard-gallery__thumb-img');
      if (existingThumbImg) existingThumbImg.src = imageUrl;
      this.setGalleryIndex(this._previewMediaIndex);
      return;
    }

    const thumbsContainer = this.querySelector('[data-gallery-thumbs]');
    if (!thumbsContainer) {
      // Single-image product — no thumb strip exists. Just swap the main image.
      this.galleryMainImg.removeAttribute('srcset');
      this.galleryMainImg.removeAttribute('sizes');
      this.galleryMainImg.src = imageUrl;
      this.galleryMainImg.alt = 'Your personalized preview';
      return;
    }

    const previewMedia = { id: 'preview', src: imageUrl, alt: 'Your personalized preview' };
    let newIndex;
    if (prepend) {
      // Insert at the front. Every existing media + thumb shifts up one index,
      // so bump the existing thumbs' data-thumb-index (their click handlers read
      // it live, so updating the attribute is enough to keep them pointing right).
      this.mediaList.unshift(previewMedia);
      newIndex = 0;
      thumbsContainer.querySelectorAll('[data-thumb-index]').forEach((el) => {
        const t = /** @type {HTMLElement} */ (el);
        const i = parseInt(t.dataset.thumbIndex || '', 10);
        if (!Number.isNaN(i)) t.dataset.thumbIndex = String(i + 1);
      });
    } else {
      newIndex = this.mediaList.length;
      this.mediaList.push(previewMedia);
    }

    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'wizard-gallery__thumb';
    thumb.setAttribute('data-thumb-index', String(newIndex));
    thumb.setAttribute('data-media-id', 'preview');
    thumb.setAttribute('aria-label', 'View your personalized preview');

    const thumbImg = document.createElement('img');
    thumbImg.className = 'wizard-gallery__thumb-img';
    thumbImg.src = imageUrl;
    thumbImg.alt = 'Your personalized preview';
    thumbImg.loading = 'lazy';
    thumb.appendChild(thumbImg);

    // Read the index live so the handler stays correct if indices shift later.
    thumb.addEventListener('click', () => this.setGalleryIndex(parseInt(thumb.dataset.thumbIndex || '0', 10)));
    if (prepend) {
      thumbsContainer.insertBefore(thumb, thumbsContainer.firstChild);
    } else {
      thumbsContainer.appendChild(thumb);
    }

    this.galleryThumbs = this.querySelectorAll('[data-thumb-index]');
    this._previewMediaIndex = newIndex;

    // setGalleryIndex swaps the main image and smooth-scrolls the thumb strip
    // (a horizontal scroll on the strip container — never scrolls the page).
    this.setGalleryIndex(newIndex);
  }

  scrollGalleryToVariant() {
    const variant = this.findMatchingVariant();
    if (!variant?.featured_image?.src) return;

    const getFilename = (url) => url.split('?')[0].split('/').pop();
    const variantFilename = getFilename(variant.featured_image.src);

    const index = this.mediaList.findIndex(m => {
      return getFilename(m.src) === variantFilename;
    });

    if (index >= 0) {
      this.setGalleryIndex(index);
    }
  }

  setupFrameTooltips() {
    let activeTooltip = null;

    const dismiss = () => {
      if (activeTooltip) {
        activeTooltip.remove();
        activeTooltip = null;
      }
    };

    this.querySelectorAll('[data-frame-tooltip]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        dismiss();

        const tip = document.createElement('div');
        tip.className = 'wizard-frame-tooltip';
        tip.textContent = btn.dataset.frameTooltip;
        btn.closest('.wizard-frame-card__info').appendChild(tip);
        activeTooltip = tip;

        document.addEventListener('click', dismiss, { once: true });
      });
    });
  }

  /* ── Sticky Nav ── */

  setupStickyNav() {
    if (!this.navEl) return;

    this.navSpacer = document.createElement('div');
    this.navSpacer.className = 'wizard__nav-spacer';
    this.navEl.parentNode.insertBefore(this.navSpacer, this.navEl.nextSibling);

    this.navSentinel = document.createElement('div');
    this.navSentinel.style.height = '1px';
    this.navSentinel.style.width = '100%';
    this.navEl.parentNode.insertBefore(this.navSentinel, this.navEl);

    this.navRow = this.navEl.querySelector('.wizard-nav__row');
    this._navMargins = '';
    this._navObserver = null;

    // Heights come from measuring the nav, never from a constant. The old 72px
    // spacer matched neither the in-page nav nor the bar, so the page jumped by
    // the difference every time the bar attached or let go (124px on a 390px
    // phone), and its 72px release margin assumed the button row sat at the
    // top of the nav, which stopped being true once the Preview button moved
    // in above it. measureNav() re-reads everything whenever the nav changes
    // size (including a rotation) and on every step change.
    this._navResizeObserver = new ResizeObserver(() => this.measureNav());
    this._navResizeObserver.observe(this.navEl);

    // The fixed bar carries its own copy of the quantity hint. While the page
    // copy is on screen too (just after scrolling past the buttons), the bar
    // drops its copy rather than show the same sentence twice.
    const pageHint = this.querySelector('[data-quantity-hint-page]');
    if (pageHint) {
      this._pageHintObserver = new IntersectionObserver(([entry]) => {
        this.navEl.classList.toggle('wizard__nav--hint-in-page', entry.isIntersecting);
      });
      this._pageHintObserver.observe(pageHint);
    }
    this.measureNav();
  }

  /**
   * A document keydown listener that lets go of this wizard once it has left
   * the page. The in-store station mounts a new wizard per product and never
   * reloads, so a plain listener would keep every old wizard (and its preview
   * images) in memory all day. A wizard that is only moved stays connected
   * and keeps its listener.
   */
  addDocumentKeydown(handler) {
    const listener = (e) => {
      if (!this.isConnected) {
        document.removeEventListener('keydown', listener);
        return;
      }
      handler(e);
    };
    document.addEventListener('keydown', listener);
  }

  disconnectedCallback() {
    // A wizard mounted more than once (the in-store station in frontend#229
    // mounts one per product) must not leave observers behind on old nodes.
    this._navResizeObserver?.disconnect();
    this._navObserver?.disconnect();
    this._pageHintObserver?.disconnect();
    this._flagsUnsubscribe?.();
  }

  /**
   * Re-measure the in-page nav and re-arm the observer that fixes it.
   *
   * All read from the nav as it sits IN THE PAGE:
   *  - its full height, which the spacer copies (--wizard-nav-height), so the
   *    content below does not move when the nav leaves the flow or returns.
   *    Only the bar's own content is in the nav; what sits under the buttons
   *    in the page is a sibling (.wizard__nav-after), so the spacer never
   *    shows as a blank band when the bar attaches;
   *  - how far below the sentinel the button row ends: the bottom root
   *    margin. Coming up from below, the bar lets go only once the in-page
   *    Add to Cart would be fully on screen;
   *  - how far below the sentinel the row starts, with the sticky header's
   *    height: the top root margin. Scrolling past, the bar attaches once
   *    NAV_ATTACH_UNDER_HEADER of the row has slid under the header. At that
   *    moment what the spacer stands in for is the rest of the row only, so
   *    the blank band is under 40px, and the in-page button is hidden for at
   *    most that many pixels of scroll.
   * Plus the bar's height while it is fixed (--bottom-bar-height on <body>),
   * which assets/scroll-under-header.js reads to keep a scrolled-to field
   * clear of it.
   *
   * While the nav is fixed its in-page shape is read by dropping the sticky
   * class and restoring it in the same task, so nothing is painted in between.
   */
  measureNav() {
    const nav = this.navEl;
    if (!nav || !this.navSentinel) {
      return;
    }
    // No bar at all: at 750px and wider (a phone rotated to landscape
    // included; the desktop layout keeps the nav in the page), or when the
    // nav is not rendered, as on a booth product between events, which hides
    // the whole buying path (sections/product-wizard.liquid). Drop the bar
    // state and publish no bar height, or the sticky-only rules and every
    // scroll that clears the bar would keep acting on one that is not there.
    if (window.innerWidth >= 750 || !nav.getClientRects().length) {
      this._navObserver?.disconnect();
      this._navObserver = null;
      this._navMargins = '';
      this.setNavSticky(/* sticky */ false);
      document.body.style.setProperty('--bottom-bar-height', '0px');
      return;
    }
    const sticky = nav.classList.contains('wizard__nav--sticky');
    const barHeight = sticky && getComputedStyle(nav).position === 'fixed'
      ? Math.round(nav.getBoundingClientRect().height)
      : 0;
    if (sticky) {
      nav.classList.remove('wizard__nav--sticky');
    }
    const sentinelTop = this.navSentinel.getBoundingClientRect().top;
    const navRect = nav.getBoundingClientRect();
    // getComputedStyle is live: read the margins before the class goes back.
    const navStyle = getComputedStyle(nav);
    const outer = navRect.height + parseFloat(navStyle.marginTop) + parseFloat(navStyle.marginBottom);
    const rowRect = (this.navRow || nav).getBoundingClientRect();
    if (sticky) {
      nav.classList.add('wizard__nav--sticky');
    }

    this.style.setProperty('--wizard-nav-height', `${Math.round(outer)}px`);
    document.body.style.setProperty('--bottom-bar-height', `${barHeight}px`);

    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 0;
    const bottom = Math.round(rowRect.bottom - sentinelTop + 8);
    // May be positive (the root reaches above the viewport): the row sits
    // well below the sentinel once the Preview button is above it.
    const top = Math.round(rowRect.top - sentinelTop + NAV_ATTACH_UNDER_HEADER - headerHeight);
    const margins = `${top}px 0px -${Math.max(0, bottom)}px 0px`;
    if (margins === this._navMargins) {
      return;
    }
    this._navMargins = margins;
    this._navObserver?.disconnect();
    // Sticky whenever the in-page button row is not on screen: scrolled past
    // it (above the header) OR not reached yet (below the fold), which covers
    // the top of the page, the gallery and the benefit sections. The sentinel
    // sits directly above the nav, so the margins turn "sentinel inside the
    // adjusted viewport" into "row on screen" in both directions: the CTA
    // should always be reachable.
    this._navObserver = new IntersectionObserver(
      (entries) => {
        if (window.innerWidth >= 750) {
          this.setNavSticky(/* sticky */ false);
          return;
        }
        entries.forEach(entry => this.setNavSticky(!entry.isIntersecting));
      },
      { threshold: 0, rootMargin: margins }
    );
    this._navObserver.observe(this.navSentinel);
  }

  setNavSticky(sticky) {
    this.navEl.classList.toggle('wizard__nav--sticky', sticky);
    this.navSpacer.classList.toggle('wizard__nav-spacer--active', sticky);
  }

  /* ── Photo Guide Modal ── */

  setupPhotoGuide() {
    const modal = this.querySelector('[data-photo-guide]');
    if (!modal) return;

    // Open triggers
    this.querySelectorAll('[data-photo-guide-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
      });
    });

    // Close button
    const closeBtn = modal.querySelector('[data-photo-guide-close]');
    closeBtn?.addEventListener('click', () => {
      modal.hidden = true;
      document.body.style.overflow = '';
    });

    // Click overlay to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.hidden = true;
        document.body.style.overflow = '';
      }
    });

    // Escape key
    this.addDocumentKeydown((e) => {
      if (e.key === 'Escape' && !modal.hidden) {
        modal.hidden = true;
        document.body.style.overflow = '';
      }
    });
  }

  /* ── "Send Photo Later" Reminder (Google compliance) ── */

  /**
   * Which personalization inputs are currently unsatisfied.
   * Mirrors validateCurrentStep()'s photo/required-text checks so the reminder
   * fires for exactly the cases that used to hard-block Add to Cart.
   * @returns {{ missingPhotoZones: Element[], missingNameInputs: Element[], anyUploading: boolean, uploadingZone: Element|null }}
   */
  getMissingPersonalization() {
    const result = { missingPhotoZones: [], missingNameInputs: [], anyUploading: false, uploadingZone: null };

    this.querySelectorAll('[data-upload]').forEach(zone => {
      if (this.isSlotHidden(zone)) return; // unrevealed multi-pet groups aren't required
      const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';
      const state = this.uploads[fieldLabel];
      if (!state) {
        result.missingPhotoZones.push(zone);
      } else if (state.uploading || !state.upload_id) {
        // In flight or not yet confirmed — a genuine wait state, not a skip.
        result.anyUploading = true;
        if (!result.uploadingZone) result.uploadingZone = zone;
      }
    });

    this.querySelectorAll('[data-line-item-property][required]').forEach(input => {
      if (this.isSlotHidden(input)) return; // skip hidden slots (incl. wrapper-level hidden)
      if (!input.value.trim()) result.missingNameInputs.push(input);
    });

    return result;
  }

  /** True once the one-time reminder has been shown this session. */
  deferReminderShown() {
    if (this._deferReminded) return true;
    try {
      return sessionStorage.getItem('instame_photo_defer_reminded') === '1';
    } catch (_) {
      return false;
    }
  }

  markDeferReminderShown() {
    this._deferReminded = true; // in-memory guard survives even if storage throws
    try {
      sessionStorage.setItem('instame_photo_defer_reminded', '1');
    } catch (_) {}
  }

  setupDeferModal() {
    const modal = this.querySelector('[data-photo-defer-modal]');
    if (!modal) return;

    modal.querySelector('[data-photo-defer-upload]')?.addEventListener('click', () => {
      this.closeDeferModal();
      this.scrollToFirstMissing();
    });

    modal.querySelector('[data-photo-defer-later]')?.addEventListener('click', async () => {
      this.closeDeferModal();
      // Second guard: the popup path above stops this modal from opening at
      // all, but a deferred add is unfulfillable at a booth, so the button
      // that performs it refuses too rather than trusting one check.
      if (window.__instamePopupSession === true) {
        this.scrollToFirstMissing();
        return;
      }
      const variant = this.findMatchingVariant();
      if (!variant) {
        this.showCartError('Please select all options.');
        return;
      }
      // Same per-count remap as the main add path — a deferred add with 2+
      // pets already filled should still buy the matching per-count product.
      const mapped = this.getMappedTarget() ? await this.resolveMappedForCart() : null;
      if (mapped === REMAP_UNRESOLVED) {
        // Blocked, not substituted: adding `variant` here would put the page
        // product in the cart carrying the extra pets' assets. The notice in
        // the nav says it once; a cart error below it would say it twice.
        this.showRemapUnavailable();
        return;
      }
      this.submitCart(mapped ? mapped.variant : variant, { deferred: true, mapped: mapped });
    });

    // Overlay click + Escape close (same pattern as the photo guide modal).
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeDeferModal();
    });
    this.addDocumentKeydown((e) => {
      if (e.key === 'Escape' && !modal.hidden) this.closeDeferModal();
    });
  }

  showDeferModal() {
    // Mark shown up front so the reminder can never appear twice, regardless of
    // how it's dismissed (either button, overlay, or Escape).
    this.markDeferReminderShown();
    const modal = this.querySelector('[data-photo-defer-modal]');
    if (!modal) {
      // Markup missing (shouldn't happen) — fail safe by never blocking: proceed
      // with a deferred add so checkout is still reachable.
      const variant = this.findMatchingVariant();
      if (variant) this.submitCart(variant, { deferred: true });
      return;
    }
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-photo-defer-upload]')?.focus();
  }

  closeDeferModal() {
    const modal = this.querySelector('[data-photo-defer-modal]');
    if (modal) modal.hidden = true;
    document.body.style.overflow = '';
  }

  /** Draw the customer's attention to the first thing they skipped. */
  scrollToFirstMissing() {
    const missing = this.getMissingPersonalization();
    if (missing.missingPhotoZones.length) {
      this.wiggleZone(missing.missingPhotoZones[0]);
    } else if (missing.missingNameInputs.length) {
      const input = missing.missingNameInputs[0];
      input.focus({ preventScroll: true });
      input.classList.add('wizard-field__input--error');
      setTimeout(() => input.classList.remove('wizard-field__input--error'), 1500);
      const isName = /^pet name/i.test(input.dataset.lineItemProperty || '');
      this.revealAboveBar(this.showFieldError(input, isName
        ? "Enter a name to continue."
        : 'Fill in this field to continue.'));
    }
  }

  /* ── Photo Upload ── */

  setupPhotoUploads() {
    const uploadZones = this.querySelectorAll('[data-upload]');
    uploadZones.forEach(zone => this.initUploadZone(zone));
    this.initCarousels(uploadZones);
  }

  initUploadZone(zone) {
    const dropzone = zone.querySelector('[data-upload-dropzone]');
    const fileInput = zone.querySelector('[data-upload-input]');
    const preview = zone.querySelector('[data-upload-preview]');
    const removeBtn = zone.querySelector('[data-upload-remove]');
    const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';

    if (!dropzone || !fileInput) return;

    dropzone.addEventListener('click', (e) => {
      if (e.target === fileInput) return;
      this.trackCustomizationStarted();
      fileInput.value = '';
      fileInput.click();
    });

    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropzone.classList.add('wizard-upload__dropzone--dragover');
    });
    dropzone.addEventListener('dragleave', () => {
      dropzone.classList.remove('wizard-upload__dropzone--dragover');
    });
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropzone.classList.remove('wizard-upload__dropzone--dragover');
      const file = e.dataTransfer?.files[0];
      if (file) this.handleFile(file, zone, fieldLabel);
    });

    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      // Clear before handleFile so re-picking the same file still fires
      // 'change' next time (browsers skip the event when the value is
      // unchanged). The captured File stays valid after the reset.
      fileInput.value = '';
      if (file) this.handleFile(file, zone, fieldLabel);
    });

    // Store original placeholder HTML so we can restore it after remove / upload-failure
    const placeholder = dropzone?.querySelector('.wizard-upload__placeholder');
    zone._originalPlaceholderHTML = placeholder?.innerHTML || '';

    removeBtn?.addEventListener('click', () => {
      const uploadData = this.uploads[fieldLabel];
      if (uploadData?._previewUrl) {
        URL.revokeObjectURL(uploadData._previewUrl);
      }
      uploadData?._abort?.abort();
      // Delete the row + Storage object only for FRESH uploads (the
      // ones we created in this session — identified by _previewUrl).
      // Tile selections point at confirmed photos that other slots /
      // future sessions may want to reuse, so we leave those alone.
      if (uploadData?._previewUrl && uploadData?.upload_id) {
        window.InstaMePhotoUpload.deleteUpload(uploadData.upload_id, {
          userId: this.getUserId(),
        });
        this.removePriorUploadById(uploadData.upload_id);
      }
      delete this.uploads[fieldLabel];
      if (preview) preview.hidden = true;
      if (dropzone) dropzone.hidden = false;
      if (placeholder) placeholder.innerHTML = zone._originalPlaceholderHTML;
      fileInput.value = '';
      const nameInput = this.getPetNameInputForSlot(fieldLabel);
      if (nameInput) nameInput.value = '';
      this.updatePreviewButtonState();
      this.revertGalleryIfNeeded();
      this.refreshMultiPetUI();
    });
  }

  /* ── Prior-Uploads Carousel ── */

  async initCarousels(zones) {
    // The station owns its session-bound photo pool and restores it itself.
    if (window.__instameInStoreSession === true) return;
    try {
      const uploads = await this.fetchPriorUploads();
      if (uploads && uploads.length) {
        this.priorUploads = uploads;
        zones.forEach(zone => {
          const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';
          // If the user already dropped a file before the fetch resolved, keep that slot in dropzone mode
          // so their in-flight upload and preview aren't hidden mid-flight.
          if (this.uploads[fieldLabel]) return;
          this.activateCarousel(zone);
        });
      }
    } catch (_) {
      // silent fallback to dropzone
    } finally {
      // Apply any P2A prefill regardless of whether the carousel rendered — the
      // pet name can still be pre-filled in dropzone mode even with no tiles.
      this.applyP2APrefill(zones);
      // Carousels (and any prefilled selection) just appeared — sync the
      // multi-pet duplicate dimming and price/button state to them.
      this.refreshMultiPetUI();
    }
  }

  /**
   * Pre-select the pet photo + pre-fill the pet name the shopper last used in
   * the Photo-to-Art flow, when they arrived here by clicking a P2A upsell card.
   * The marker is a one-shot sessionStorage entry written by photo-to-art.js;
   * we consume (remove) it so it never leaks into later, unrelated navigations.
   * Only the first pet slot is filled — P2A is single-pet.
   * @param {NodeListOf<HTMLElement> | HTMLElement[]} zones
   */
  applyP2APrefill(zones) {
    let prefill;
    try {
      const raw = sessionStorage.getItem('instame_p2a_prefill');
      if (!raw) return;
      sessionStorage.removeItem('instame_p2a_prefill'); // one-shot
      prefill = JSON.parse(raw);
    } catch (_) {
      return;
    }
    if (!prefill || typeof prefill !== 'object') return;

    const zone = zones[0];
    if (!zone) return;
    const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';
    // Don't clobber an upload the user already started in this session.
    if (this.uploads[fieldLabel]) return;

    const petName = prefill.petName || '';
    let tileSelected = false;
    if (prefill.uploadId && zone.dataset.uploadMode === 'carousel') {
      tileSelected = this.preselectTileByUploadId(zone, prefill.uploadId, petName);
    }
    // Fill the name even when the photo tile wasn't found (e.g. dropzone mode,
    // or the photo aged out of the prior-uploads list).
    if (!tileSelected && petName) {
      const nameInput = /** @type {HTMLInputElement | null} */ (this.getPetNameInputForSlot(fieldLabel));
      if (nameInput) nameInput.value = petName;
    }
  }

  /**
   * Programmatically select a carousel tile by its upload id, mirroring the
   * state changes selectTile() makes but WITHOUT the user-action side effects
   * (analytics, customization_started). Returns false if the tile isn't present.
   * @param {HTMLElement} zone
   * @param {string} uploadId
   * @param {string} petName
   * @returns {boolean}
   */
  preselectTileByUploadId(zone, uploadId, petName) {
    const carousel = zone.querySelector('[data-upload-carousel]');
    if (!carousel) return false;
    const tile = /** @type {HTMLElement | null} */ (
      carousel.querySelector(`[data-upload-tile][data-upload-id="${CSS.escape(uploadId)}"]`)
    );
    if (!tile) return false;

    const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';
    const name = petName || tile.dataset.petName || '';

    this.uploads[fieldLabel] = {
      upload_id: uploadId,
      photo_url: tile.dataset.photoUrl,
      photo_path: tile.dataset.photoPath,
      pet_name: name,
      uploading: false,
      selectSeq: ((this.uploads[fieldLabel] && this.uploads[fieldLabel].selectSeq) || 0) + 1,
    };

    const nameInput = /** @type {HTMLInputElement | null} */ (this.getPetNameInputForSlot(fieldLabel));
    // Set the value directly; intentionally do NOT dispatch 'input' so this
    // system prefill doesn't fire the customization_started analytics event.
    if (nameInput) nameInput.value = name;

    this.updateTileSelection(zone, uploadId);
    this.updatePreviewButtonState();
    return true;
  }

  async fetchPriorUploads() {
    try {
      await window.HillsdaleCustomer?.ready();
      const backendUrl = this.getBackendUrl();
      const params = new URLSearchParams({ user_id: this.getUserId() });
      const res = await fetch(`${backendUrl}/api/photos/list?${params}`, {headers:window.HillsdaleCustomer?.headers() || {}});
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data.uploads) ? data.uploads : [];
    } catch (_) {
      return [];
    }
  }

  activateCarousel(zone) {
    const carousel = zone.querySelector('[data-upload-carousel]');
    const dropzone = zone.querySelector('[data-upload-dropzone]');
    const preview = zone.querySelector('[data-upload-preview]');
    if (!carousel) return;

    this.renderCarousel(zone);
    carousel.hidden = false;
    if (dropzone) dropzone.hidden = true;
    if (preview) preview.hidden = true;
    zone.dataset.uploadMode = 'carousel';
    this.bindCarouselEvents(zone);
  }

  renderCarousel(zone) {
    const carousel = zone.querySelector('[data-upload-carousel]');
    if (!carousel) return;

    const tiles = this.priorUploads.map(u => this.buildTileHTML(u)).join('');
    carousel.innerHTML = `
      <div class="wizard-upload__carousel-scroll" data-upload-carousel-scroll>
        <button type="button" class="wizard-upload__tile wizard-upload__tile--plus" data-upload-tile-plus aria-label="Upload new photo">
          <svg class="wizard-upload__tile-plus-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          <span class="wizard-upload__tile-spinner"></span>
        </button>
        ${tiles}
      </div>
    `;
  }

  buildTileHTML(u) {
    const name = (u.pet_name || '').replace(/"/g, '&quot;');
    return `
      <button type="button" class="wizard-upload__tile" data-upload-tile
              data-upload-id="${u.upload_id}"
              data-photo-url="${u.photo_url}"
              data-photo-path="${u.photo_path}"
              data-pet-name="${name}"
              draggable="false"
              aria-label="Select pet photo">
        <img src="${u.photo_url}" alt="" loading="lazy" width="120" height="120" draggable="false">
      </button>
    `;
  }

  bindCarouselEvents(zone) {
    const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';
    const carousel = zone.querySelector('[data-upload-carousel]');
    const fileInput = zone.querySelector('[data-upload-input]');
    if (!carousel || !fileInput) return;

    const plusTile = carousel.querySelector('[data-upload-tile-plus]');

    // + tile → file picker
    plusTile?.addEventListener('click', () => {
      if (plusTile.getAttribute('aria-busy') === 'true') return;
      this.trackCustomizationStarted();
      fileInput.value = '';
      fileInput.click();
    });

    // + tile → drag & drop
    plusTile?.addEventListener('dragover', (e) => {
      e.preventDefault();
      plusTile.classList.add('wizard-upload__tile--dragover');
    });
    plusTile?.addEventListener('dragleave', () => {
      plusTile.classList.remove('wizard-upload__tile--dragover');
    });
    plusTile?.addEventListener('drop', (e) => {
      e.preventDefault();
      plusTile.classList.remove('wizard-upload__tile--dragover');
      const file = e.dataTransfer?.files[0];
      if (file) this.handleFile(file, zone, fieldLabel);
    });

    // Tile selection (event delegation — tiles may be re-rendered).
    // Suppressed when the click is the tail of a drag-to-scroll gesture.
    carousel.addEventListener('click', (e) => {
      if (carousel._suppressNextClick) {
        carousel._suppressNextClick = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const tile = e.target.closest('[data-upload-tile]');
      if (!tile) return;
      this.selectTile(tile, zone, fieldLabel);
    });

    // Drag-to-scroll on desktop. Mobile already scrolls via touch.
    const scroller = carousel.querySelector('[data-upload-carousel-scroll]');
    if (scroller) this.bindCarouselDragScroll(scroller, carousel);
  }

  bindCarouselDragScroll(scroller, carousel) {
    // Mouse-only drag-to-scroll. Touch is already handled by native horizontal
    // overflow scrolling (-webkit-overflow-scrolling: touch).
    let isDown = false;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = 0;
    const DRAG_THRESHOLD = 5; // px before we treat it as a drag (and suppress click)

    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      e.preventDefault();
      scroller.scrollLeft = startScrollLeft - dx;
    };

    const onUp = () => {
      if (!isDown) return;
      isDown = false;
      scroller.classList.remove('wizard-upload__carousel-scroll--dragging');
      document.body.classList.remove('wizard-carousel-dragging');
      // If the user actually dragged, the upcoming click event on the tile is
      // unintended — suppress it once.
      if (moved > DRAG_THRESHOLD) carousel._suppressNextClick = true;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    scroller.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return; // primary button only
      // Stop text selection and native HTML5 drag (the image-ghost / page-shift
      // artifact). Click events still fire on mouseup, so tile selection works.
      e.preventDefault();
      isDown = true;
      moved = 0;
      startX = e.clientX;
      startScrollLeft = scroller.scrollLeft;
      scroller.classList.add('wizard-upload__carousel-scroll--dragging');
      // Defensive: contain horizontal overscroll at the document level so the
      // macOS back-swipe gesture can't trigger if any input escapes our handler.
      document.body.classList.add('wizard-carousel-dragging');
      // Listen on document so the drag survives the cursor leaving the strip.
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    // Belt-and-suspenders: block native image/element drag inside the scroller.
    scroller.addEventListener('dragstart', (e) => e.preventDefault());
  }

  selectTile(tile, zone, fieldLabel) {
    // Multi-pet: one photo can only be one pet. The backend also rejects
    // duplicate upload_ids across slots — stop the mistake before it's made.
    if (this.multiPetActive) {
      const takenBy = this.getSelectedUploadIdsByLabel().get(tile.dataset.uploadId);
      if (takenBy && takenBy !== fieldLabel) {
        if (window.__instameInStoreSession === true) {
          this.swapPhotoSlots(fieldLabel, takenBy);
          return;
        }
        const slotNum = (takenBy.match(/(\d+)$/) || [])[1] || '1';
        this.showUploadError(zone, `This photo is already Pet ${slotNum} — pick a different one. Another picture of the same pet works great!`);
        return;
      }
    }

    const existing = this.uploads[fieldLabel];
    const mySeq = (existing?.selectSeq || 0) + 1;

    // Abort any in-flight upload and release its blob URL
    existing?._abort?.abort();
    if (existing?._previewUrl) URL.revokeObjectURL(existing._previewUrl);
    // If we're switching away from a fresh upload (one we created this
    // session — has _previewUrl), delete it from the backend. Skip if the
    // user clicked the same tile that represents this fresh upload.
    if (existing?._previewUrl && existing?.upload_id && existing.upload_id !== tile.dataset.uploadId) {
      window.InstaMePhotoUpload.deleteUpload(existing.upload_id, {
        userId: this.getUserId(),
      });
      this.removePriorUploadById(existing.upload_id);
    }

    const uploadId = tile.dataset.uploadId;
    const inStore = window.__instameInStoreSession === true;
    const switchingPhoto = Boolean(existing?.upload_id) && existing.upload_id !== uploadId;
    // In store, save the typed name to the photo being left before switching,
    // so re-selecting that photo brings its name back.
    if (inStore && switchingPhoto && !existing.uploading) void this.saveUploadName(fieldLabel, { force: true });
    // Each photo carries its own name. A name typed before any photo was
    // chosen stays with the first photo picked; switching photos never
    // carries the previous pet's name over.
    const petName = tile.dataset.petName || (inStore && !switchingPhoto
      ? this.getPetNameInputForSlot(fieldLabel)?.value || '' : '');

    this.uploads[fieldLabel] = {
      upload_id: uploadId,
      photo_url: tile.dataset.photoUrl,
      photo_path: tile.dataset.photoPath,
      pet_name: petName,
      uploading: false,
      selectSeq: mySeq,
    };

    // Always overwrite pet name input (per design)
    const nameInput = this.getPetNameInputForSlot(fieldLabel);
    if (nameInput) {
      nameInput.value = petName;
      nameInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.updateTileSelection(zone, uploadId);
    this.clearUploadError(zone);
    this.updatePreviewButtonState();
    this.revertGalleryIfNeeded();
    this.refreshMultiPetUI();
    this.trackCustomizationStarted();
    this.trackUploadEvent('photo_upload_tile_selected', {
      product_id: this.productData.id,
      had_pet_name: !!petName,
    });
  }

  swapPhotoSlots(targetLabel, sourceLabel) {
    const target = this.uploads[targetLabel];
    const source = this.uploads[sourceLabel];
    if (!source?.upload_id || target?.uploading || source.uploading) return;
    const nameFor = (label, state) => this.getPetNameInputForSlot(label)?.value ?? state?.pet_name ?? '';
    const targetName = nameFor(targetLabel, target), sourceName = nameFor(sourceLabel, source);
    // Update both slots before emitting input events, so station persistence never sees duplicates.
    this.uploads[targetLabel] = { ...source, pet_name: sourceName, selectSeq: (target?.selectSeq || 0) + 1 };
    if (target?.upload_id) this.uploads[sourceLabel] = { ...target, pet_name: targetName, selectSeq: (source.selectSeq || 0) + 1 };
    else delete this.uploads[sourceLabel];
    for (const label of [targetLabel, sourceLabel]) {
      const input = this.getPetNameInputForSlot(label);
      if (input) input.value = this.uploads[label]?.pet_name || '';
      const group = this.petGroups.find(item => item.fieldLabel === label);
      if (group) { this.updateTileSelection(group.zone, this.uploads[label]?.upload_id); this.clearUploadError(group.zone); }
    }
    this.revertGalleryIfNeeded();
    this.approvedVariantId = null;
    this.refreshMultiPetUI();
    this.updatePreviewButtonState();
    for (const label of [targetLabel, sourceLabel]) {
      this.getPetNameInputForSlot(label)?.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  isPreviewOption(name) {
    // Product configuration can override this classification. Frame/size/volume stay outside.
    const configured = this.productData.previewOptionNames;
    if (Array.isArray(configured)) return configured.includes(name);
    return !/frame|framing|size|volume|capacity/i.test(name) && /color|colour|background/i.test(name);
  }

  configureInStorePreviewOptions() {
    if (this._previewOptionsConfigured || !this.productData.previewRendered) return;
    this._previewOptionsConfigured = true;
    const indicators = [...this.stepIndicators];
    const kept = [];
    for (const step of this.steps) {
      const input = step.querySelector('.wizard-swatch__input');
      const indicator = indicators.find(item => item.dataset.stepIndicator === step.dataset.step);
      if (input && this.isPreviewOption(input.name)) {
        step.hidden = true;
        step.dataset.step = 'preview-option';
        if (indicator) indicator.hidden = true;
      } else {
        step.dataset.step = String(kept.length + 1);
        if (indicator) {
          indicator.dataset.stepIndicator = step.dataset.step;
          const number = indicator.querySelector?.('.wizard-progress__number');
          if (number) number.textContent = step.dataset.step;
        }
        kept.push(step);
      }
    }
    this.steps = kept;
    this.stepIndicators = indicators.filter(item => !item.hidden);
    this.totalSteps = kept.length;
    this.currentStep = 1;
  }

  updateTileSelection(zone, uploadId) {
    const carousel = zone.querySelector('[data-upload-carousel]');
    if (!carousel) return;
    carousel.querySelectorAll('[data-upload-tile]').forEach(t => {
      t.classList.toggle('wizard-upload__tile--selected', t.dataset.uploadId === uploadId);
    });
  }

  /** Pull a tile out of the carousel DOM (all zones) and the in-memory
   *  priorUploads array. Used after firing DELETE for an abandoned fresh
   *  upload so the tile doesn't linger as a phantom. */
  removePriorUploadById(uploadId) {
    if (!uploadId) return;
    this.priorUploads = this.priorUploads.filter(u => u.upload_id !== uploadId);
    this.querySelectorAll(`[data-upload-tile][data-upload-id="${uploadId}"]`).forEach(t => t.remove());
  }

  insertFreshTile(zone, uploadRecord) {
    const scroll = zone.querySelector('[data-upload-carousel-scroll]');
    if (!scroll) return;
    const plusTile = scroll.querySelector('[data-upload-tile-plus]');
    const wrapper = document.createElement('div');
    wrapper.innerHTML = this.buildTileHTML(uploadRecord).trim();
    const newTile = wrapper.firstElementChild;
    if (plusTile && plusTile.nextSibling) {
      scroll.insertBefore(newTile, plusTile.nextSibling);
    } else {
      scroll.appendChild(newTile);
    }
    this.updateTileSelection(zone, uploadRecord.upload_id);
  }

  setPlusTileLoading(zone, loading) {
    const plusTile = zone.querySelector('[data-upload-tile-plus]');
    if (!plusTile) return;
    plusTile.setAttribute('aria-busy', loading ? 'true' : 'false');
  }

  revertGalleryIfNeeded() {
    // The accepted preview lives as its own thumb in the gallery — leave it in place.
    // Just clear the line-item-property tracking so a re-uploaded photo doesn't carry the stale preview into cart.
    if (!this.previewImageUrl) return;
    this.previewImageUrl = null;
    this.previewId = null;
    this.approvedPrintSignature = null;
    this.approvedPreviewText = null;
    this._approvalOutdated = false;
    // On a booth product this is also the moment Add to Cart closes again —
    // the approval it was unlocked by has just been discarded.
    this.updateBoothApprovalState();
  }

  clearUploadError(zone) {
    const err = zone.querySelector('[data-upload-error]');
    if (err) err.hidden = true;
  }

  updatePreviewButtonState() {
    // Instant Preview is marked not-ready while any slot is uploading. The other
    // preconditions (photo present + name filled) are handled by
    // instant-preview.js::monitorInputs.
    //
    // aria-disabled, not `disabled`: the button has to stay clickable so a tap
    // explains itself. openPreview() recomputes readiness from live state
    // instead of reading this attribute, so even if the clearing branch below
    // never runs, a stale mark can only grey the button out. It cannot block a
    // preview.
    const btn = this.querySelector('[data-preview-btn]');
    if (!btn) return;
    const anyUploading = Object.values(this.uploads).some(u => u && u.uploading);
    if (anyUploading) {
      btn.setAttribute('aria-disabled', 'true');
      btn.dataset.uploadingDisabled = '1';
    } else if (btn.dataset.uploadingDisabled) {
      delete btn.dataset.uploadingDisabled;
      // Re-evaluate by triggering an input event that instant-preview.js listens for
      this.querySelector('[data-line-item-property]')?.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /* ── Multi-pet painted door (exp_multi_pet_interest, backend issue #77) ── */

  /** Show the interest button on single-photo products without multi-pet
   *  support (flag-gated, latched once per page load like multiPetActive).
   *  Click opens the coming-soon dialog — no photo slot ever opens, so the
   *  door costs the shopper one tap at most. */
  setupMultiPetInterest() {
    this.interestBtn = this.querySelector('[data-multi-pet-interest]');
    const modal = this.querySelector('[data-interest-modal]');
    if (!this.interestBtn || !modal) return;
    const eligible =
      !this.multiPetActive &&
      (this.petGroups || []).length === 1 &&
      !MULTI_PET_PRODUCTS.some(p => p.id === this.productData.id) &&
      !MULTI_PET_INTEREST_EXCLUDED.has(this.productData.id) &&
      (window.instameExperiments || {})['exp_multi_pet_interest'] === 'test';
    if (!eligible) return;

    this.interestBtn.hidden = false;
    this._interestCount = null;

    // shown: once per product per session, on first scroll into view —
    // the honest denominator for click-through.
    const shownKey = 'instame_mpi_shown_' + this.productData.id;
    let shownFired = false;
    try { shownFired = sessionStorage.getItem(shownKey) === '1'; } catch (_) {}
    if (!shownFired && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries.some(e => e.isIntersecting)) {
          io.disconnect();
          try { sessionStorage.setItem(shownKey, '1'); } catch (_) {}
          this.trackInterest('multi_pet_interest_shown', null);
        }
      });
      io.observe(this.interestBtn);
    }

    this.interestBtn.addEventListener('click', () => {
      this.trackInterest('multi_pet_interest_clicked', null);
      this.openInterestModal();
    });

    modal.querySelectorAll('[data-interest-count]').forEach((chip) => {
      chip.addEventListener('click', () => {
        modal.querySelectorAll('[data-interest-count]').forEach((c) => c.setAttribute('aria-pressed', 'false'));
        chip.setAttribute('aria-pressed', 'true');
        this._interestCount = chip.dataset.interestCount;
        this.trackInterest('multi_pet_interest_count_selected', { requested_count: this._interestCount });
      });
    });

    modal.querySelector('[data-interest-notify]')?.addEventListener('click', () => this.submitInterestEmail());
    modal.querySelector('[data-interest-email]')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.submitInterestEmail();
      }
    });
    // The cross-sell offers a multi-pet portrait "today". On a booth product
    // that has to mean the BOOTH portrait: the markup's default points at the
    // online one, which ships and cannot be collected at the tent — and adding
    // it beside a booth pickup item lands the customer in Shopify's mixed-cart
    // dead end at the counter. The booth set covers the same 1-4 pets.
    //
    // The destination is resolved once, here, and used for BOTH the href and
    // the tracked property. They were previously hardcoded independently, so an
    // href-only fix would have left the painted-door dashboard reporting the
    // online URL forever.
    const crosssell = modal.querySelector('[data-interest-crosssell]');
    const destination = this.productData.boothOnly
      ? '/products/custom-watercolor-one-pet-portrait-booth'
      : '/products/custom-one-pet-portrait';
    if (crosssell) {
      crosssell.setAttribute('href', destination);
    }
    crosssell?.addEventListener('click', () => {
      // instameTrack uses keepalive, so the event survives the navigation.
      this.trackInterest('multi_pet_interest_crosssell_clicked', {
        requested_count: this._interestCount,
        destination,
      });
    });
    modal.querySelector('[data-interest-dismiss]')?.addEventListener('click', () => this.closeInterestModal());
    modal.addEventListener('click', (e) => {
      if (e.target === modal) this.closeInterestModal();
    });
    this.addDocumentKeydown((e) => {
      if (e.key === 'Escape' && !modal.hidden) this.closeInterestModal();
    });
  }

  /** Interest events go through the first-party dual-write path — a
   *  Supabase user_events row AND the PostHog mirror — so the readout
   *  survives ad blockers and lives in our own DB, not just PostHog. */
  trackInterest(eventType, metadata) {
    if (typeof window.instameTrack !== 'function') return;
    window.instameTrack(eventType, {
      product_id: this.productData.id,
      metadata: metadata,
    });
  }

  openInterestModal() {
    const modal = this.querySelector('[data-interest-modal]');
    if (!modal) return;
    // A reopen after a submitted email shows the ✓ state for the session —
    // keyed per product so another design's door still captures its own
    // email + requested count.
    let saved = false;
    try { saved = sessionStorage.getItem('instame_mpi_email_saved_' + this.productData.id) === '1'; } catch (_) {}
    this.setInterestSubmitted(saved);
    // Signed-in Shopify customers get their email pre-filled (frontend#98) —
    // still editable, and nothing sends until they tap "Notify me". Only an
    // empty input is filled so a typed value is never clobbered.
    if (!saved) {
      const emailInput = modal.querySelector('[data-interest-email]');
      const customerEmail = typeof window.__instameCustomerEmail === 'string'
        ? window.__instameCustomerEmail.trim()
        : '';
      if (emailInput && !emailInput.value && customerEmail) {
        emailInput.value = customerEmail;
      }
    }
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    modal.querySelector('[data-interest-dismiss]')?.focus();
  }

  closeInterestModal() {
    const modal = this.querySelector('[data-interest-modal]');
    if (!modal) return;
    modal.hidden = true;
    document.body.style.overflow = '';
    this.interestBtn?.focus();
  }

  setInterestSubmitted(saved) {
    const modal = this.querySelector('[data-interest-modal]');
    if (!modal) return;
    const confirmEl = modal.querySelector('[data-interest-confirm]');
    const emailRow = modal.querySelector('[data-interest-email-row]');
    const lede = modal.querySelector('[data-interest-lede]');
    if (confirmEl) confirmEl.hidden = !saved;
    if (emailRow) emailRow.hidden = saved;
    if (lede) lede.hidden = saved;
  }

  submitInterestEmail() {
    const modal = this.querySelector('[data-interest-modal]');
    const input = modal?.querySelector('[data-interest-email]');
    const email = (input?.value || '').trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      input?.focus();
      return;
    }
    // Durable capture into the users table via the shared identity helper
    // (email_history entry tagged with this source) — the store the launch
    // email exports from. The event itself carries no raw email. Autofill
    // for this input ships with the frontend#98 work, deliberately not here.
    if (typeof window.instameIdentify === 'function') {
      window.instameIdentify(email, null /* phone */, { source: 'multi_pet_interest' });
    }
    this.trackInterest('multi_pet_interest_email_submitted', {
      requested_count: this._interestCount,
    });
    try { sessionStorage.setItem('instame_mpi_email_saved_' + this.productData.id, '1'); } catch (_) {}
    this.setInterestSubmitted(true);
  }

  /** True when the test arm has a revealed pet slot with no photo chosen and
   *  none in flight for it. Such a slot blocks Instant Preview AND Add to
   *  Cart until the shopper fills or removes it — deferring an empty extra
   *  slot would charge for a pet that may never exist. */
  hasEmptyVisiblePetSlot() {
    if (!this.multiPetActive || this.visiblePetCount < 2) return false;
    return (this.petGroups || []).some((group, i) => {
      if (i >= this.visiblePetCount) return false;
      const state = this.uploads[group.fieldLabel];
      return !state || (!state.upload_id && !state.uploading);
    });
  }

  getPetNameInputForSlot(fieldLabel) {
    const match = fieldLabel.match(/(\d+)$/);
    const suffixedSelector = match ? `[data-line-item-property="Pet Name ${match[1]}"]` : null;
    // Single-photo products render the input without a numeric suffix even when the
    // photo field is named "Upload Pet Photo 1" — fall back to the unsuffixed input.
    return (suffixedSelector && this.querySelector(suffixedSelector))
      || this.querySelector('[data-line-item-property="Pet Name"]');
  }

  /** Persist the currently-entered pet name onto the upload's user_uploads row. */
  async saveUploadName(fieldLabel, { force = false } = {}) {
    const state = this.uploads[fieldLabel];
    if (!state?.upload_id) return;
    const input = this.getPetNameInputForSlot(fieldLabel);
    const currentName = input?.value?.trim() || '';
    if (!force && currentName === (state.pet_name || '')) return;

    try {
      await fetch(`${this.getBackendUrl()}/api/photos/update-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(window.HillsdaleCustomer?.headers() || {}) },
        body: JSON.stringify({
          upload_id: state.upload_id,
          user_id: this.getUserId(),
          pet_name: currentName || null,
        }),
      });
      state.pet_name = currentName;
      // Keep the carousel's in-memory cache in sync so re-selecting the tile auto-fills the new name.
      const cached = this.priorUploads.find(u => u.upload_id === state.upload_id);
      if (cached) cached.pet_name = currentName;
      this.querySelectorAll(`[data-upload-tile][data-upload-id="${state.upload_id}"]`).forEach(t => {
        t.dataset.petName = currentName;
      });
    } catch (err) {
      console.warn('Failed to save pet name:', err);
      this.trackUploadEvent('pet_name_save_failed', {
        product_id: this.productData.id,
        upload_id: state.upload_id || null,
        message: err && (err.message || String(err)),
      });
    }
  }

  async saveAllUploadNames() {
    await Promise.all(Object.keys(this.uploads).map(label => this.saveUploadName(label)));
  }

  getBackendUrl() {
    const inStoreBackend = document.querySelector('#instame-in-store-ipad')?.dataset.backendUrl;
    if (inStoreBackend) return inStoreBackend;
    return (location.hostname === '127.0.0.1' || location.hostname === 'localhost')
      ? 'http://localhost:3000'
      : 'https://api.instame.shop';
  }

  async handleFile(file, zone, fieldLabel) {
    this.clearUploadError(zone);
    this.trackCustomizationStarted();
    this.revertGalleryIfNeeded();

    const fileExt = (file.name.match(/\.(\w+)$/i) || [])[1]?.toLowerCase() || 'unknown';
    const mimeType = file.type || 'unknown';
    const originalSizeBytes = file.size || 0;
    const startedAt = Date.now();

    // Seed slot state with an incrementing selectSeq so late-arriving responses can self-cancel.
    const existing = this.uploads[fieldLabel];
    existing?._abort?.abort();
    if (existing?._previewUrl) URL.revokeObjectURL(existing._previewUrl);
    // If we're replacing a fresh upload (one we created this session — has
    // _previewUrl), delete it from the backend. Tile selections share their
    // upload_id with the carousel, so leave those alone.
    if (existing?._previewUrl && existing?.upload_id) {
      window.InstaMePhotoUpload.deleteUpload(existing.upload_id, {
        userId: this.getUserId(),
      });
      this.removePriorUploadById(existing.upload_id);
    }
    const mySeq = (existing?.selectSeq || 0) + 1;
    const abort = new AbortController();
    this.uploads[fieldLabel] = { uploading: true, selectSeq: mySeq, fileName: file.name, _abort: abort };

    // Fresh uploads always start without a name — clear whatever was carried over from a prior
    // tile selection. The user types a fresh name after upload completes.
    const nameInput = this.getPetNameInputForSlot(fieldLabel);
    if (nameInput) nameInput.value = '';

    this.updatePreviewButtonState();
    this.trackUploadEvent('photo_upload_started', {
      format: mimeType,
      extension: fileExt,
      original_size_mb: +(originalSizeBytes / 1024 / 1024).toFixed(2),
      product_id: this.productData.id,
    });

    const isCarousel = zone.dataset.uploadMode === 'carousel';
    const dropzone = zone.querySelector('[data-upload-dropzone]');
    const placeholder = dropzone?.querySelector('.wizard-upload__placeholder');

    if (isCarousel) {
      this.setPlusTileLoading(zone, true);
    } else if (placeholder) {
      placeholder.innerHTML = '<span class="wizard-upload__text">Processing photo...</span>';
    }

    let result;
    try {
      result = await window.InstaMePhotoUpload.processAndUpload(file, {
        userId: this.getUserId(),
        extraFields: {
          group_id: crypto.randomUUID(),
          product_id: String(this.productData.id),
        },
        signal: abort.signal,
      });
    } catch (err) {
      if (err?.code === 'ABORTED') {
        // Aborted by a tile selection (slot state has uploading:false) —
        // clear the + tile spinner, or its aria-busy guard blocks the file
        // picker forever. A superseding upload (uploading:true) owns the
        // spinner and clears it itself.
        if (!this.uploads[fieldLabel]?.uploading) {
          this.setPlusTileLoading(zone, false);
        }
        return;
      }
      console.error('[product-wizard] upload failed:', err.code || err.name, err.technical || err.message, err);
      this.trackUploadEvent('photo_upload_error', {
        error_code: err.code || 'UNKNOWN',
        format: mimeType,
        extension: fileExt,
        original_size_mb: +(originalSizeBytes / 1024 / 1024).toFixed(2),
        retryable: !!err.retryable,
        duration_ms: Date.now() - startedAt,
        message: err.technical || err.message,
        product_id: this.productData.id,
      });
      if (this.uploads[fieldLabel]?.selectSeq === mySeq) {
        this.showUploadError(zone, err.userMessage || err.message || 'Upload failed. Please try again.');
        this.restoreZoneIdle(zone, fieldLabel, mySeq);
      }
      return;
    }

    // Late-response guard — user replaced/removed the file while we were processing.
    if (this.uploads[fieldLabel]?.selectSeq !== mySeq) return;

    // Build the dropzone preview from the processed file (HEIC → JPEG / etc.).
    // Showing the preview only after the upload lands is intentional — it
    // signals "upload succeeded" so the user never sees a preview followed
    // by a "couldn't upload" error, and there's no in-between state where
    // a Replace click would try to DELETE an upload_id we don't have yet.
    const previewUrl = URL.createObjectURL(result.processedFile);
    if (!isCarousel) {
      const previewImg = zone.querySelector('[data-upload-preview-img]');
      const preview = zone.querySelector('[data-upload-preview]');
      if (previewImg) previewImg.src = previewUrl;
      if (preview) preview.hidden = false;
      if (dropzone) dropzone.hidden = true;
    }

    this.uploads[fieldLabel] = {
      upload_id: result.upload_id,
      photo_url: result.photo_url,
      photo_path: result.photo_path,
      pet_name: '', // fresh upload starts nameless — synced via saveUploadName() on IP/ATC
      fileName: file.name,
      uploading: false,
      selectSeq: mySeq,
      _previewUrl: previewUrl, // keep for GC on next replace
    };

    this.trackUploadEvent('photo_upload_success', {
      format: mimeType,
      extension: fileExt,
      detected_mime: result.detectedMime || null,
      was_converted: result.wasConverted,
      was_compressed: result.wasCompressed,
      original_size_mb: +(originalSizeBytes / 1024 / 1024).toFixed(2),
      final_size_mb: +((result.finalSize || 0) / 1024 / 1024).toFixed(2),
      duration_ms: Date.now() - startedAt,
      product_id: this.productData.id,
    });

    try {
      if (window.instameTrack) {
        var currentVariant = this.findMatchingVariant();
        window.instameTrack('photo_uploaded', {
          product_id: this.productData.id,
          variant_id: this.variantInput?.value,
          metadata: {
            pet_name: this.uploads[fieldLabel].pet_name || null,
            upload_id: result.upload_id,
            product_title: this.productData.title || null,
            variant_title: currentVariant ? currentVariant.title : null,
            options: currentVariant ? this.getVariantOptions(currentVariant) : null
          }
        });
      }
    } catch (_) {}

    if (isCarousel) {
      this.setPlusTileLoading(zone, false);
      this.priorUploads.unshift({
        upload_id: result.upload_id,
        photo_url: result.photo_url,
        photo_path: result.photo_path,
        pet_name: '',
        created_at: new Date().toISOString(),
      });
      this.insertFreshTile(zone, this.priorUploads[0]);
    }

    this.updatePreviewButtonState();
    this.refreshMultiPetUI();
  }

  /** Restore a zone to idle state after processing/upload failure (carousel or dropzone). */
  restoreZoneIdle(zone, fieldLabel, mySeq) {
    if (this.uploads[fieldLabel]?.selectSeq !== mySeq) return;
    const state = this.uploads[fieldLabel];
    if (state?._previewUrl) URL.revokeObjectURL(state._previewUrl);
    delete this.uploads[fieldLabel];

    const isCarousel = zone.dataset.uploadMode === 'carousel';
    if (isCarousel) {
      this.setPlusTileLoading(zone, false);
    } else {
      const dropzone = zone.querySelector('[data-upload-dropzone]');
      const preview = zone.querySelector('[data-upload-preview]');
      const placeholder = dropzone?.querySelector('.wizard-upload__placeholder');
      if (preview) preview.hidden = true;
      if (dropzone) dropzone.hidden = false;
      if (placeholder && zone._originalPlaceholderHTML) {
        placeholder.innerHTML = zone._originalPlaceholderHTML;
      }
    }
    const fileInput = zone.querySelector('[data-upload-input]');
    if (fileInput) fileInput.value = '';
    this.updatePreviewButtonState();
    this.refreshMultiPetUI();
  }

  /** Build a {Color: 'White', Frame: 'Brown', Size: '11x14'} object from a
   *  variant + the product's option list. Used by every analytics event
   *  fired after a variant is known so analysts can filter on individual
   *  options without parsing variant_title strings.
   *  @param {object} [product] defaults to the page product; pass the mapped
   *    per-count sibling when the variant belongs to it. */
  getVariantOptions(variant, product) {
    var out = {};
    var productData = product || this.productData;
    if (!variant || !productData || !Array.isArray(productData.options)) return out;
    productData.options.forEach(function (opt, idx) {
      var name = (opt && typeof opt === 'object') ? opt.name : opt;
      if (name && Array.isArray(variant.options) && variant.options[idx] != null) {
        out[name] = variant.options[idx];
      }
    });
    return out;
  }

  /** Fire view_item once on every product page load with the default variant. */
  trackViewItem() {
    try {
      var variant = this.findMatchingVariant() || this.productData.currentVariant;
      if (!window.instameTrack) return;
      window.instameTrack('view_item', {
        product_id: this.productData.id,
        variant_id: variant ? variant.id : null,
        metadata: {
          product_title: this.productData.title || null,
          variant_title: variant ? variant.title : null,
          options: variant ? this.getVariantOptions(variant) : null,
          price: variant ? variant.price : null,
          product_image: (this.mediaList[0] && this.mediaList[0].src) || null
        }
      });
    } catch (_) {}
  }

  /** Fire variant_selected once per option group on the current step when
   *  the user clicks NEXT (i.e. confirms their choice). Each option group
   *  on the step generates one event so analyses can attribute confirmation
   *  per option independently. */
  trackVariantSelectedForCurrentStep() {
    try {
      if (!window.instameTrack) return;
      var stepEl = this.querySelector('[data-step="' + this.currentStep + '"]');
      if (!stepEl) return;

      // Collect distinct option group names on this step, skipping internal
      // toggles like _frame_mode that aren't real product variants.
      var groupNames = new Set();
      stepEl.querySelectorAll('.wizard-swatch__input').forEach(function (input) {
        if (!input.name.startsWith('_')) groupNames.add(input.name);
      });
      if (groupNames.size === 0) return;

      var variant = this.findMatchingVariant();
      var optionsObj = variant ? this.getVariantOptions(variant) : {};

      groupNames.forEach((groupName) => {
        var value = this.selectedOptions[groupName];
        if (!value) return;
        window.instameTrack('variant_selected', {
          product_id: this.productData.id,
          variant_id: variant ? variant.id : null,
          metadata: {
            product_title: this.productData.title || null,
            option_name: groupName,
            option_value: value,
            variant_title: variant ? variant.title : null,
            options: optionsObj,
            price: variant ? variant.price : null,
            step: this.currentStep
          }
        });
      });
    } catch (_) {}
  }

  /** Track customization_started once per product per session */
  trackCustomizationStarted() {
    try {
      var key = 'instame_cust_' + this.productData.id;
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
      if (window.instameTrack) {
        var variant = this.findMatchingVariant();
        window.instameTrack('customization_started', {
          product_id: this.productData.id,
          variant_id: variant ? variant.id : null,
          metadata: {
            product_title: this.productData.title || null,
            variant_title: variant ? variant.title : null,
            options: variant ? this.getVariantOptions(variant) : null,
            product_image: (this.mediaList[0] && this.mediaList[0].src) || null
          }
        });
      }
    } catch (_) {}
  }

  /** Fire a PostHog event. Silent no-op if posthog unavailable. */
  trackUploadEvent(eventName, properties = {}) {
    try {
      if (window.posthog?.capture) {
        window.posthog.capture(eventName, properties);
      }
    } catch (e) {
      console.warn('PostHog tracking error:', e);
    }
  }

  showUploadError(zone, message) {
    const errorEl = zone.querySelector('[data-upload-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  wiggleZone(zone) {
    const target = zone.dataset.uploadMode === 'carousel'
      ? zone.querySelector('[data-upload-carousel]')
      : zone.querySelector('[data-upload-dropzone]');
    if (!target) return;
    target.classList.add('wizard-upload__dropzone--error');
    setTimeout(() => target.classList.remove('wizard-upload__dropzone--error'), 400);
    // The whole zone, so its error line comes into view with it. block:
    // 'nearest' alone knew nothing of the fixed bar and parked both under it
    // when the tap came from the bar.
    this.revealAboveBar(zone);
  }

  /**
   * Scroll just far enough to show `el` between the sticky header and the
   * fixed mobile bar, and not at all when it already shows. Shared with
   * product-info accordions through assets/scroll-under-header.js, which knows
   * both heights; the plain scroll is only for a page that did not load it.
   * @param {Element|null} el
   */
  revealAboveBar(el) {
    if (!el) {
      return;
    }
    if (window.instameScrollUnderHeader) {
      window.instameScrollUnderHeader(el, { nearest: true });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Say, under a text field, what it needs. Stays until the field is edited:
   * a red outline alone (all a missing name used to get) says something is
   * wrong but not what.
   * @param {HTMLInputElement} input
   * @param {string} message
   * @returns {Element} the field, message included, for revealAboveBar
   */
  showFieldError(input, message) {
    const field = input.closest('.wizard-field') || input.parentElement;
    let el = field.querySelector('[data-field-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'wizard-field__error';
      el.setAttribute('data-field-error', '');
      el.setAttribute('role', 'alert');
      input.insertAdjacentElement('afterend', el);
      input.addEventListener('input', () => { el.hidden = true; });
    }
    el.textContent = message;
    el.hidden = false;
    return field;
  }

  /**
   * Put a message in the Instant Preview hint line for a few seconds. The
   * hint is hidden in the fixed bar except while it carries one of these
   * (the --flash class), so a tap from the bar still gets its answer there.
   * @param {string} message
   */
  flashPreviewHint(message) {
    const hint = this.querySelector('[data-preview-hint]');
    if (!hint) {
      return;
    }
    if (hint.dataset.defaultText === undefined) {
      hint.dataset.defaultText = hint.textContent;
    }
    hint.hidden = false;
    hint.textContent = message;
    hint.classList.add('wizard-preview__hint--flash');
    clearTimeout(this._previewHintTimer);
    this._previewHintTimer = setTimeout(() => {
      hint.textContent = hint.dataset.defaultText;
      hint.classList.remove('wizard-preview__hint--flash');
    }, 2500);
  }

  /* ── Multi-Pet Slots ── */

  setupMultiPet() {
    const zones = Array.from(this.querySelectorAll('[data-upload]'));
    this.petGroups = zones.map((zone) => {
      const fieldLabel = zone.dataset.fieldLabel || 'Pet Photo';
      const wrapper = zone.closest('.wizard-field');
      // Metaobject configs may render "Pet Name N" as its own SIBLING
      // .wizard-field instead of nesting it inside the photo group — resolve
      // it explicitly so hide/reveal always moves the pair together. Exact
      // suffix match only: getPetNameInputForSlot's unsuffixed fallback could
      // point a later group at group 1's name field and hide the wrong thing.
      const suffix = (fieldLabel.match(/(\d+)$/) || [])[1];
      const nameInput = suffix
        ? this.querySelector(`[data-line-item-property="Pet Name ${suffix}"]`)
        : null;
      const nameField = nameInput ? nameInput.closest('.wizard-field') : null;
      return {
        zone,
        fieldLabel,
        wrapper,
        siblingNameField: (nameField && wrapper && !wrapper.contains(nameField)) ? nameField : null,
      };
    });

    const own = MULTI_PET_PRODUCTS.find(p => p.id === this.productData.id);
    const isPerCountProduct = !!own;
    const fixedPetCount = Number(this.productData.petSlotCount) || 0;

    // GRADUATED. exp_preview_multi_pet ran to a decision and the test arm is
    // now the product: every per-count portrait reveals slots and remaps.
    // There is no flag read left here, so nothing can drop a shopper into the
    // old single-slot behaviour — not a slow PostHog response, not a blocked
    // request, not a stale cached bundle.
    //
    // THE BOOTH CLAUSE THAT USED TO SIT HERE IS GONE, and that is only safe
    // because the flag read went with it. It existed because booth products
    // must never wait on PostHog: a phone that cannot reach the flag endpoint
    // (weak signal at a tent) fell to control, where the booth 1-pet product
    // showed one slot with no way to add a pet — the booth multi-pet portraits
    // unbuyable exactly where they are sold. If this is ever re-gated on a
    // flag, that clause has to come back with it, or that failure returns
    // silently.
    // A fixed-count duplicate has no sibling to remap to, but still needs
    // the multi-pet preview path so every selected photo reaches the artwork.
    this.multiPetActive = this.petGroups.length > 0 && (isPerCountProduct || fixedPetCount > 1);

    // visiblePetCount tracks the control arm too so getFilledPetCount() can
    // stamp pet_count on analytics for both arms.
    this.visiblePetCount = this.petGroups.length;

    if (!this.multiPetActive) {
      // Control arm must stay visually identical to TODAY even after the
      // uniform multi-group personalization config lands in admin: a
      // per-count product shows exactly its own pet count of groups, and any
      // extra rendered groups are hidden (validation skips them via
      // isSlotHidden). Without this, flipping the metaobject would show a
      // 1-pet shopper four required photo fields and misfire the defer modal.
      // A product OUTSIDE the hardcoded list gets no `own`, so nothing was
      // hidden and every rendered slot stayed required. That is invisible until
      // someone duplicates a per-count portrait: the copy is in neither list, so
      // a one-pet shopper meets four mandatory photo fields and cannot proceed.
      // instame.pet_slot_count lets the product say how many slots it wants, so
      // any future duplicate is correct by data rather than by remembering to
      // edit this file. Falls back to the list, so per-count products are
      // unchanged.
      const slotCount = own ? own.petCount : Number(this.productData.petSlotCount) || 0;
      if (slotCount > 0 && this.petGroups.length > slotCount) {
        this.visiblePetCount = slotCount;
        this.petGroups.forEach((group, i) => {
          this.setPetGroupHidden(group, i >= slotCount);
        });
      }
      return; // control arm: exactly today's behavior
    }

    // Start at the page product's OWN pet count, not always 1.
    //
    // Starting at 1 unconditionally was correct only on the 1-pet entry
    // product. On a 2/3/4-pet page — reachable by tapping that line in the
    // cart to redo a photo, by a shared URL, or by ordinary catalogue browsing
    // — it showed ONE slot for a product the customer had already bought as a
    // multi-pet portrait. getMappedTarget() returns null below two slots, so
    // no remap corrected it: one photo bought the multi-pet product at the
    // multi-pet price.
    //
    // Deliberately NOT fixed by allowing a down-remap to the 1-pet sibling:
    // that would silently move the customer onto a different product, which is
    // the exact behaviour the block above exists to remove.
    //
    // On the 1-pet entry product this is 1, so the experiment's entry flow is
    // unchanged; capped by rendered groups so a product with fewer groups than
    // its nominal count cannot reveal a slot that does not exist.
    //
    // Same helper the remove path floors against, so the starting count and the
    // minimum can never disagree.
    this.visiblePetCount = this.minVisiblePetCount();
    this.petGroups.forEach((group, i) => {
      this.setPetGroupHidden(group, i >= this.visiblePetCount);
    });

    this.addPetBtn = this.querySelector('[data-add-pet]');
    this.addPetBtn?.addEventListener('click', () => this.revealNextPetGroup());

    this.petGroups.forEach((group, i) => {
      const removeBtn = group.wrapper?.querySelector('[data-remove-pet]');
      removeBtn?.addEventListener('click', () => this.removePetGroup(i));
    });

    // Any pet-set mutation invalidates a saved preview. Photo add/replace/
    // remove already clear the stamps (revertGalleryIfNeeded in handleFile /
    // selectTile / the remove paths); a RENAME changes the composite's name
    // layer just the same, so it must also force a fresh "Looks Good".
    this.petGroups.forEach((group) => {
      const nameInput = this.getPetNameInputForSlot(group.fieldLabel);
      nameInput?.addEventListener('input', () => this.revertGalleryIfNeeded());
    });

    this.refreshMultiPetUI();
  }

  setPetGroupHidden(group, hidden) {
    if (group.wrapper) group.wrapper.hidden = hidden;
    if (group.siblingNameField) group.siblingNameField.hidden = hidden;
  }

  /** True when el sits inside a pet slot the shopper can't see (test-arm
   *  groups start hidden until "Add another pet" reveals them). The hidden
   *  attribute lives on the GROUP wrapper, which a plain
   *  closest('.wizard-field').hidden check on the paired name input misses —
   *  its own .wizard-field is a visible child of the hidden wrapper. */
  isSlotHidden(el) {
    return !!el.closest('.wizard-field[hidden]');
  }

  /** Re-derive everything that depends on visible-slot / filled-pet state.
   *  Cheap no-op in the control arm. */
  refreshMultiPetUI() {
    if (!this.multiPetActive) return;
    this.updateRemoveAffordances();
    this.updateDuplicateTileStates();
    // Repaints the page product's price, then overlays the mapped per-count
    // product and refreshes the add-button price hint.
    this.updatePrice();
    // Preview readiness depends on every visible filled slot in the test arm
    // (instant-preview.js), and slot reveal/remove doesn't fire the input
    // events its observer listens for — re-check explicitly.
    this.instantPreview?._checkPreviewReady?.();
  }

  /** Visible filled slots' upload states, in group order, each tagged with its
   *  LITERAL slot number — a deferred/empty middle slot leaves a gap so later
   *  entries keep the number of the customer-visible positional properties
   *  ("Pet Name N"). Drives the batch fulfillment-assets call. */
  getOrderedFilledUploads() {
    const list = [];
    (this.petGroups || []).forEach((group, i) => {
      if (group.wrapper?.hidden) {
        return;
      }
      const state = this.uploads[group.fieldLabel];
      if (state && state.upload_id && !state.uploading) {
        // The number every other consumer reads off the LABEL — setupMultiPet
        // pairs "…Photo N" with "Pet Name N", cart-products.liquid resolves a
        // row's photo from "…Photo N" → _upload_link_N. Taking it from the DOM
        // position instead would only agree while the product's personalization
        // metaobject happens to list its groups in order; position stays as the
        // fallback for a single unnumbered "Pet Photo" label.
        const labelSlot = (group.fieldLabel.match(/(\d+)$/) || [])[1];
        list.push({ state, slot: labelSlot ? Number(labelSlot) : i + 1 });
      }
    });
    return list;
  }

  /** Reveal cap: every rendered group, never more than 4 pets. */
  getMaxPetGroups() {
    const rendered = Math.min(this.petGroups ? this.petGroups.length : 0, 4 /* maxPets */);
    const fixed = Number(this.productData.petSlotCount) || 0;
    return !petSiblings(this.productData.id).length && fixed > 1
      ? Math.min(rendered, fixed) : rendered;
  }

  revealNextPetGroup() {
    if (!this.multiPetActive) return;
    if (this.visiblePetCount >= this.getMaxPetGroups()) return;
    this.visiblePetCount++;
    this.setPetGroupHidden(this.petGroups[this.visiblePetCount - 1], false /* hidden */);
    this.trackUploadEvent('multi_pet_group_added', {
      product_id: this.productData.id,
      visible_groups: this.visiblePetCount,
      pet_count: this.getFilledPetCount(),
    });
    this.refreshMultiPetUI();
  }

  /** Remove any VISIBLE pet group while 2+ are visible (the last remaining
   *  pet can never be removed). The removed slot's upload is discarded
   *  (in-flight aborted; fresh uploads deleted server-side, same rules as the
   *  dropzone remove button); later visible groups' photo selections and
   *  typed names SHIFT UP one slot — labels stay positional ("Pet Photo N"
   *  never moves) — then the last visible group empties out and hides.
   *  Removing slot 1 is the same shift, just starting at index 0: the old
   *  slot 2 becomes the new slot 1, so unsuffixed-at-ATC / preview-anchoring
   *  semantics re-derive from the new slot 1 state.
   *  @param {number} index 0-based index into this.petGroups
   */
  removePetGroup(index) {
    if (!this.multiPetActive) return;
    if (this.visiblePetCount <= this.minVisiblePetCount()) {
      return;
    }
    if (index < 0 || index >= this.visiblePetCount) {
      return;
    }

    const removedGroup = this.petGroups[index];
    const removedState = this.uploads[removedGroup.fieldLabel];

    removedState?._abort?.abort();
    if (removedState?._previewUrl) {
      URL.revokeObjectURL(removedState._previewUrl);
    }
    // Fresh uploads (created this session — identified by _previewUrl) are
    // deleted server-side; tile selections stay reusable for other slots.
    if (removedState?._previewUrl && removedState?.upload_id) {
      window.InstaMePhotoUpload.deleteUpload(removedState.upload_id, {
        userId: this.getUserId(),
      });
      this.removePriorUploadById(removedState.upload_id);
    }

    // Snapshot the slots AFTER the removed one; slots before it are untouched.
    const shifted = [];
    for (let i = index + 1; i < this.visiblePetCount; i++) {
      const group = this.petGroups[i];
      const state = this.uploads[group.fieldLabel] || null;
      const nameInput = this.getPetNameInputForSlot(group.fieldLabel);
      let carried = state;
      if (state?.uploading) {
        // An in-flight upload can't survive a shift: its completion callback
        // is bound to the old slot's label/zone and would land the photo in
        // the wrong pet. Abort it and shift the slot up empty.
        state._abort?.abort();
        carried = null;
      }
      shifted.push({ state: carried, name: nameInput ? nameInput.value : '' });
    }

    // Write the shifted slots back one position up; past the end of the
    // shifted list this clears the slot (the vacated tail).
    for (let i = index; i < this.visiblePetCount; i++) {
      const incoming = shifted[i - index] || null;
      this.applySlotState(this.petGroups[i], incoming ? incoming.state : null, incoming ? incoming.name : '');
    }
    this.visiblePetCount--;
    this.setPetGroupHidden(this.petGroups[this.visiblePetCount], true /* hidden */);

    this.revertGalleryIfNeeded();
    this.updatePreviewButtonState();
    this.trackUploadEvent('multi_pet_group_removed', {
      product_id: this.productData.id,
      removed_slot: index + 1,
      visible_groups: this.visiblePetCount,
      pet_count: this.getFilledPetCount(),
    });
    this.refreshMultiPetUI();
  }

  /** Point a slot's state + visible UI at `state` (or empty it when null).
   *  `nameValue` is written into the slot's name input as-is — typed-but-
   *  unsaved names must survive a shift. Bumps selectSeq past the slot's old
   *  counter so straggling async work keyed to this label self-cancels. */
  applySlotState(group, state, nameValue) {
    const zone = group.zone;
    const fieldLabel = group.fieldLabel;
    const prevSeq = this.uploads[fieldLabel]?.selectSeq || 0;

    if (state && state.upload_id) {
      this.uploads[fieldLabel] = Object.assign({}, state, { selectSeq: prevSeq + 1 });
    } else {
      delete this.uploads[fieldLabel];
    }

    const nameInput = this.getPetNameInputForSlot(fieldLabel);
    if (nameInput) nameInput.value = nameValue || '';

    const dropzone = zone.querySelector('[data-upload-dropzone]');
    const preview = zone.querySelector('[data-upload-preview]');
    const previewImg = zone.querySelector('[data-upload-preview-img]');
    const fileInput = zone.querySelector('[data-upload-input]');
    const placeholder = dropzone?.querySelector('.wizard-upload__placeholder');
    if (fileInput) fileInput.value = '';

    if (zone.dataset.uploadMode === 'carousel') {
      this.setPlusTileLoading(zone, false /* loading */);
      if (state && state.upload_id) {
        // The receiving carousel may lack a tile for a fresh upload made in
        // another slot (insertFreshTile only adds to the uploading zone).
        if (!zone.querySelector(`[data-upload-tile][data-upload-id="${CSS.escape(state.upload_id)}"]`)) {
          this.insertFreshTile(zone, {
            upload_id: state.upload_id,
            photo_url: state.photo_url,
            photo_path: state.photo_path,
            pet_name: state.pet_name || '',
          });
        }
        this.updateTileSelection(zone, state.upload_id);
      } else {
        this.updateTileSelection(zone, null /* uploadId — clears the selection */);
      }
    } else {
      if (state && state.upload_id) {
        if (previewImg) previewImg.src = state._previewUrl || state.photo_url || '';
        if (preview) preview.hidden = false;
        if (dropzone) dropzone.hidden = true;
      } else {
        if (preview) preview.hidden = true;
        if (dropzone) dropzone.hidden = false;
        if (placeholder && zone._originalPlaceholderHTML) {
          placeholder.innerHTML = zone._originalPlaceholderHTML;
        }
      }
    }
    this.clearUploadError(zone);
  }

  /** The fewest slots this page may show: the page product's own pet count.
   *
   *  On the 1-pet entry product that is 1, so removing down to a single pet
   *  behaves exactly as before. On a 2/3/4-pet page it stops the shopper
   *  reducing below what that product IS. Without the floor, a 4-pet page
   *  could be taken down to one slot, and because no remap fires below two
   *  slots the 4-pet product would ship with a single photo at the 4-pet
   *  price — the same corrupted order the block above exists to prevent,
   *  reached by hand instead of by a failed lookup.
   *
   *  The alternative, remapping DOWN to the matching smaller product, is
   *  deliberately not taken: it would silently move the shopper onto a
   *  different product, which is what this whole change removes. */
  minVisiblePetCount() {
    const own = MULTI_PET_PRODUCTS.find(p => p.id === this.productData.id);
    const fixed = Number(this.productData.petSlotCount) || 0;
    return own ? Math.min(own.petCount, this.petGroups.length)
      : fixed > 1 ? Math.min(fixed, this.petGroups.length) : 1;
  }

  /** "✕ remove" shows on every visible group while more than the page
   *  product's own pet count are visible; at the floor there is nothing to
   *  remove, so no group offers the control. */
  updateRemoveAffordances() {
    const floor = this.minVisiblePetCount();
    (this.petGroups || []).forEach((group, i) => {
      const removeBtn = group.wrapper?.querySelector('[data-remove-pet]');
      if (!removeBtn) return;
      // Groups reveal in order, so "visible" ⇔ index < visiblePetCount.
      removeBtn.hidden = !(this.multiPetActive && this.visiblePetCount > floor && i < this.visiblePetCount);
    });
  }

  /** upload_id → field label of the visible slot currently holding it. */
  getSelectedUploadIdsByLabel() {
    const map = new Map();
    (this.petGroups || []).forEach((group) => {
      if (group.wrapper?.hidden) return;
      const state = this.uploads[group.fieldLabel];
      if (state?.upload_id) map.set(state.upload_id, group.fieldLabel);
    });
    return map;
  }

  /** Dim carousel tiles already selected as ANOTHER pet. selectTile() shows
   *  the inline explanation if a dimmed tile is tapped anyway. */
  updateDuplicateTileStates() {
    if (!this.multiPetActive) return;
    const selectedByLabel = this.getSelectedUploadIdsByLabel();
    this.petGroups.forEach((group) => {
      group.zone.querySelectorAll('[data-upload-tile]').forEach((tile) => {
        const takenBy = selectedByLabel.get(tile.dataset.uploadId);
        const taken = window.__instameInStoreSession !== true && takenBy !== undefined && takenBy !== group.fieldLabel;
        tile.classList.toggle('wizard-upload__tile--taken', taken);
        if (taken) {
          tile.setAttribute('aria-disabled', 'true');
        } else {
          tile.removeAttribute('aria-disabled');
        }
      });
    });
  }

  /** Confirmed uploads in VISIBLE slots — drives the per-count product
   *  mapping, and pet_count analytics in both arms. */
  getFilledPetCount() {
    let filled = 0;
    (this.petGroups || []).forEach((group) => {
      if (group.wrapper?.hidden) return;
      const state = this.uploads[group.fieldLabel];
      if (state && state.upload_id && !state.uploading) filled++;
    });
    return filled;
  }

  /** The per-count product the REVEALED slot count maps to, or null when the
   *  current page product should be used (control arm, fewer than 2 slots
   *  revealed, or already on the right product). Keyed off revealed slots —
   *  not filled photos — so the shown price changes the moment "Add Another
   *  Pet" reveals a slot (and drops back on Remove). The empty-slot gate in
   *  handleAddToCart guarantees every revealed slot is filled before a line
   *  ships, so the displayed price and the charged price always agree. */
  getMappedTarget() {
    if (!this.multiPetActive) return null;
    const count = this.visiblePetCount;
    if (count < 2) return null; // single slot: keep the page product
    const target = petSiblings(this.productData.id)
      .find(p => p.petCount === Math.min(count, 4 /* petCount cap */));
    if (!target || target.id === this.productData.id) return null;
    return target;
  }

  /** Fetch a per-count sibling product via the Ajax API. /products/<handle>.js
   *  returns prices in CENTS like the wizard's embedded productData —
   *  deliberately NOT the /products/<handle>.json endpoint bundle-upsell.js
   *  uses, which returns dollar strings and would corrupt the price math
   *  here. Successes are cached; failures are evicted so a later call retries. */
  fetchSiblingProduct(handle) {
    if (!this._siblingProducts[handle]) {
      this._siblingProducts[handle] = fetch(`/products/${handle}.js`)
        .then(res => (res.ok ? res.json() : null))
        .catch(() => null)
        .then((product) => {
          if (product) {
            this._siblingResolved[handle] = product;
          } else {
            delete this._siblingProducts[handle];
          }
          return product;
        });
    }
    return this._siblingProducts[handle];
  }

  /** '8"x10"' ⇄ '10"x8"' — the per-count portraits list the same physical
   *  sizes with portrait/landscape orientation flipped. */
  mirrorSizeValue(value) {
    const parts = String(value).split('x');
    if (parts.length !== 2) return value;
    return parts[1] + 'x' + parts[0];
  }

  /** Find the variant of a per-count sibling matching the options selected on
   *  THIS page: Background Color / Frame Option exact, Size exact first then
   *  the portrait↔landscape mirror. Returns null when nothing maps. */
  findVariantInProduct(product, selectedOptions) {
    if (!product || !Array.isArray(product.variants)) return null;
    const options = Array.isArray(product.options) ? product.options : [];
    const wanted = options.map((opt) => {
      const name = (opt && typeof opt === 'object') ? opt.name : String(opt);
      return { name: name, value: selectedOptions[name] };
    });
    if (!wanted.length || wanted.some(w => !w.value)) return null;

    const matchAt = (values) => product.variants.find(v =>
      Array.isArray(v.options) && wanted.every((w, i) => v.options[i] === values[i])
    );

    let variant = matchAt(wanted.map(w => w.value));
    if (!variant) {
      variant = matchAt(wanted.map(w =>
        /size/i.test(w.name) ? this.mirrorSizeValue(w.value) : w.value
      ));
    }
    return variant || null;
  }

  /** Resolve {product, variant} for a per-count target, or null when the
   *  sibling fetch fails / no variant maps / it's sold out. Callers keep the
   *  page product's variant in the null case (defensive by design). */
  async resolveMappedVariant(target) {
    const product = await this.fetchSiblingProduct(target.handle);
    if (!product) return null;
    const variant = this.findVariantInProduct(product, this.selectedOptions);
    if (!variant || !variant.available) return null;
    return { product: product, variant: variant };
  }

  /** ATC-time remap. Returns null when no remap applies, a resolved mapping on
   *  success, and REMAP_UNRESOLVED when a remap IS required but could not be
   *  resolved — callers must block on that rather than add to cart.
   *
   *  REVERSAL, 2026-08-13. This previously read: "Any failure returns null —
   *  the CURRENT page product's variant ships instead, with a posthog event
   *  recording the miss; a mapping problem must never block checkout."
   *
   *  That optimised for conversion while remap failure was hypothetical. What
   *  it actually shipped was a corrupted order: a shopper who configured two
   *  pets, saw a two-pet preview, and pressed Add to Cart got the ONE-pet
   *  product carrying two pets' assets at the one-pet price — an order
   *  fulfilment can never make as previewed. A blocked sale costs one sale,
   *  visibly. A substituted product costs a refund and support time, and at a
   *  booth it costs a person standing at the counter with a queue behind them
   *  while staff try to reconcile a job that does not add up.
   *
   *  The size-mismatch cause is gone (the booth 1-pet product no longer sells a
   *  size its siblings lack), so every remaining cause — sibling unfetchable,
   *  variant unavailable, blanks at zero — is a case where the sale genuinely
   *  cannot be fulfilled as previewed. Blocking is honest there; substituting
   *  is a lie in product form. */
  async resolveMappedForCart() {
    const target = this.getMappedTarget();
    if (!target) return null;
    try {
      const resolved = await this.resolveMappedVariant(target);
      if (!resolved) {
        // `unresolved` means the lookup ran and came back empty: sibling
        // unfetchable, variant unavailable, blanks at zero. A config or stock
        // condition, not a code fault.
        this.trackUploadEvent('multi_pet_variant_map_failed', {
          cause: 'unresolved',
          product_id: this.productData.id,
          target_product_id: target.id,
          target_handle: target.handle,
          pet_count: this.visiblePetCount,
          filled_pet_count: this.getFilledPetCount(),
          selected_options: Object.assign({}, this.selectedOptions),
        });
        return REMAP_UNRESOLVED;
      }
      return { product: resolved.product, variant: resolved.variant, target: target };
    } catch (err) {
      // Both exits block the customer identically, so both have to be visible.
      // This one used to return silently, which mattered less when the failure
      // merely substituted a product and the sale still completed — now it
      // stops the purchase, and it was the only way that could happen with no
      // trace at all. An alert stream reading healthy while someone at the
      // counter cannot buy is the most misleading state available.
      //
      // The cause separates the two: `exception` is a CODE fault, `unresolved`
      // is config or stock. Whoever answers the page needs to know which kind
      // of wrong they are running toward, because the responses share nothing.
      //
      // Deliberately minimal and self-guarded. The throw may well have come
      // from gathering the richer properties in the branch above — re-reading
      // that same state here could throw again, inside the catch, and take the
      // add-to-cart path down with it. `target` is safe: it is resolved before
      // the try and comes from the hardcoded product table.
      try {
        this.trackUploadEvent('multi_pet_variant_map_failed', {
          cause: 'exception',
          product_id: this.productData.id,
          target_product_id: target.id,
          target_handle: target.handle,
          error: String((err && err.message) || err),
        });
      } catch (_) {}
      return REMAP_UNRESOLVED;
    }
  }

  /** What we tell a customer when the per-count product they configured cannot
   *  be resolved. Deliberately cause-agnostic: "sibling unfetchable", "variant
   *  unavailable" and "out of blanks" all mean the same thing to a shopper, and
   *  naming the wrong one is worse than naming none. In particular it does NOT
   *  say "sold out" — the cause may be configuration, and telling someone at a
   *  tent we sold out of something still sitting on the table is its own
   *  failure.
   *
   *  At the booth there is a human three feet away, so the action is to ask.
   *  Online the shopper has to self-serve, so they get the two changes that
   *  actually restore a working path.
   *
   *  "Remove a pet" is a real path in both: getMappedTarget() returns null once
   *  the count reaches a point where the target IS the page product, or drops
   *  below two — so reducing pets always terminates in a buyable state. */
  remapUnavailableMessage() {
    const count = this.visiblePetCount;
    const own = MULTI_PET_PRODUCTS.find(p => p.id === this.productData.id);
    const lead = `We can't make a ${count}-pet version of this right now.`;
    return own && own.set === 'booth'
      ? `${lead} Please ask at the counter — we'll sort it out.`
      : `${lead} Try a different size or colour, or remove a pet to continue.`;
  }

  /** Test-arm price overlay: with 2+ slots revealed the shopper pays for the
   *  matching per-count product, so its variant drives the price text,
   *  subtotals, availability/button state and the hidden variant input.
   *  updatePrice() paints the page product synchronously first, so a slow or
   *  failed sibling fetch simply leaves that in place. */
  async applyMappedPrice() {
    const token = ++this._mappedPriceToken;
    const target = this.getMappedTarget();
    if (!target) {
      // Down to a count the page product itself covers (the notice's own
      // "remove a pet" advice), so nothing is left to be unavailable.
      this.clearRemapUnavailable();
      return;
    }
    const resolved = await this.resolveMappedVariant(target);
    if (token !== this._mappedPriceToken) return; // superseded by newer state
    if (!resolved) {
      // Tell them HERE, at the moment they reveal the slot, rather than after
      // they have uploaded photos and waited for a preview. This is the same
      // principle the booth sold-out notice already states: hide the buying
      // path itself, because the upload step is the costly one to let someone
      // start. At a booth the difference between "told at slot 2" and "told
      // after the upload" is the whole cost.
      //
      // This must NOT latch. A sibling fetch can fail transiently on weak
      // signal at a tent, and a permanently dead buying path would cost the
      // exact sale the old never-block rule existed to protect. It re-evaluates
      // for free: every option change, slot change and retry runs this again,
      // and a success (below) or a count the page product covers (above) clears
      // it, so only a fresh failure keeps the button greyed. A tap in between
      // re-resolves the product itself (addToCart, resolveMappedForCart).
      this.showRemapUnavailable();
      return;
    }
    this.clearRemapUnavailable();
    this.renderVariantPrice(resolved.variant);
  }

  /** Grey the buying path and say why. Paired with clearRemapUnavailable();
   *  see the non-latching note in applyMappedPrice().
   *
   *  aria-disabled, not `disabled` (updateBoothApprovalState paints it): a
   *  truly disabled button swallowed the tap, so the only explanation was
   *  whatever the shopper happened to read before tapping. A tap now goes
   *  through addToCart(), which re-resolves the per-count product and, if it
   *  is still unavailable, lands back here. The notice sits in the nav above
   *  the button row (it used to be squeezed INTO the row, between Back and
   *  Add to Cart), so it rides along into the fixed bar. */
  showRemapUnavailable() {
    this._remapUnavailable = true;
    this.updateBoothApprovalState();
    let notice = this.querySelector('[data-remap-unavailable]');
    if (!notice) {
      notice = document.createElement('p');
      notice.setAttribute('data-remap-unavailable', '');
      notice.setAttribute('role', 'status');
      notice.className = 'wizard-remap-unavailable';
      this.navRow?.parentNode?.insertBefore(notice, this.navRow);
    }
    notice.textContent = this.remapUnavailableMessage();
    notice.hidden = false;
  }

  clearRemapUnavailable() {
    this._remapUnavailable = false;
    this.updateBoothApprovalState();
    const notice = this.querySelector('[data-remap-unavailable]');
    if (notice) notice.hidden = true;
  }

  /** Variant price (cents) of the per-count product for `count` pets under
   *  the currently selected options; null when unknown. The page product is
   *  read locally, siblings via the cached Ajax fetch. */
  async getVariantPriceForCount(count) {
    const entry = petSiblings(this.productData.id).find(p => p.petCount === count);
    if (!entry) return null;
    if (entry.id === this.productData.id) {
      const variant = this.findMatchingVariant();
      return variant ? variant.price : null;
    }
    const resolved = await this.resolveMappedVariant(entry);
    return resolved ? resolved.variant.price : null;
  }

  /** Show/hide + label the "Add another pet" button. Visible only in the
   *  test arm while hidden groups remain (cap = rendered groups). The label
   *  carries a live price delta for the CURRENTLY selected options:
   *  next-count product price minus current-count product price. */
  async updateAddPetButton() {
    const btn = this.addPetBtn;
    if (!btn) return;
    if (!this.multiPetActive || this.visiblePetCount >= this.getMaxPetGroups()) {
      btn.hidden = true;
      return;
    }
    btn.hidden = false;
    const capText = `up to ${this.getMaxPetGroups()}`;
    btn.textContent = `Add Another Pet (${capText})`;

    const token = ++this._addPetLabelToken;
    const fromCount = this.visiblePetCount;
    const [fromPrice, toPrice] = await Promise.all([
      this.getVariantPriceForCount(fromCount),
      this.getVariantPriceForCount(fromCount + 1),
    ]);
    if (token !== this._addPetLabelToken) return; // superseded
    if (fromPrice == null || toPrice == null) return; // keep the hint-less label
    const delta = Math.max(toPrice - fromPrice, 0 /* minCents */);
    // Whole-dollar deltas read cleaner without cents ("+$2", not "+$2.00");
    // fractional deltas keep them. Label-only — real prices keep formatMoney.
    const deltaText = this.formatMoney(delta).replace(/\.00$/, '');
    btn.textContent = `Add Another Pet (${capText} · +${deltaText})`;
  }

  /** Product id/title for ATC analytics — the MAPPED per-count product when
   *  the test arm will remap, else the page product. Title is null until the
   *  sibling fetch has resolved once (it's warm in practice: the price
   *  overlay fetches as soon as the second slot is revealed). */
  getAnalyticsProductInfo() {
    const target = this.getMappedTarget();
    if (!target) {
      return { product_id: this.productData.id, product_title: this.productData.title };
    }
    const cached = this._siblingResolved[target.handle];
    return { product_id: target.id, product_title: cached ? cached.title : null };
  }

  /* ── Add to Cart ── */

  getUserId() {
    // A second tab can reset shared local storage. Keep the current order's
    // identity paired with its existing tab-scoped authorization headers.
    if (window.__instameInStoreSession === true) {
      if (!window.instameUserId) throw new Error('This design session is unavailable.');
      return window.instameUserId;
    }
    const customer = window.HillsdaleCustomer?.current();
    if (customer) return customer.user_id;
    let id = localStorage.getItem('instame_user_id');
    if (!id) {
      // user-identity.liquid initializes this on every page load,
      // but as a fallback use the same anon_ prefix format.
      id = 'anon_' + crypto.randomUUID();
      localStorage.setItem('instame_user_id', id);
    }
    return id;
  }

  /* ── Booth: approved-design gate ─────────────────────────────────────────
   *
   * A booth product is printed on site, minutes after checkout, by a person
   * with a queue in front of them. There is no art team between the order and
   * the press, so whatever the customer approved IS what gets made — and if
   * they approved nothing, the press has nothing. Online that is survivable
   * (the order lands stamped "⚠ NOT CONFIRMED" and the team makes the art
   * before it ships); at a booth it is a customer standing at the counter
   * waiting for something nobody can produce.
   *
   * So on booth products only, Add to Cart stays shut until the shopper has
   * opened Instant Preview and tapped "Looks Good".
   *
   * SCOPED TO THE PRODUCT, NOT THE SESSION. `__instamePopupSession` only means
   * "arrived through the booth QR", which survives onto ship-home products via
   * header nav and search. `instame.booth_only` means "this is made at the
   * tent", which is true however the page was reached — a direct link, a
   * restored tab, a staff device. It is also the same signal every browse
   * guard and the cart's mixed-fulfilment warning already key on.
   */
  boothApprovalRequired() {
    return this.productData?.boothOnly === true;
  }

  /** The approval token: both halves are written together in
   *  InstantPreview.acceptPreview, and only after /api/preview/save returned a
   *  row. So this cannot be true for a design the backend never stored. */
  previewApproved() {
    return !!(this.previewId && this.previewImageUrl);
  }

  /**
   * Does the CURRENT variant print the same file the approved design was
   * approved for?
   *
   * Not every option changes the print. On the booth watercolour portrait the
   * Frame Option is a real, priced line item that draws nothing (all four
   * frames share one bare-sheet base image), and a greeting card's Pack is a
   * quantity. Forcing a re-approval for those is pure friction at a counter.
   * Background Colour, by contrast, is filled straight into the printed sheet.
   *
   * The theme does not decide which is which. `/api/preview/init` returns a
   * variant_key -> print signature map built from the same two row fields
   * renderPrintComposite actually consumes, so this is a string compare
   * against ground truth rather than a guess about what an option name means.
   *
   * Returns true (changed) whenever it cannot prove otherwise — no map yet, an
   * unknown variant key, no recorded approval signature. Every uncertain case
   * degrades to "make them preview again", which costs a few seconds; the
   * opposite default costs a misprint.
   *
   * @returns {boolean} true when the approval no longer describes this variant
   */
  printSignatureChanged() {
    const approved = this.approvedPrintSignature;
    if (!approved) return true;
    const current = this.currentPrintSignature();
    if (!current) return true;
    return current !== approved;
  }

  /** Print signature of the currently selected variant, or null if unknowable. */
  currentPrintSignature() {
    const map = this.variantPrintSignatures;
    const preview = this.instantPreview;
    if (!map || !preview) return null;
    // Multi-pet remap (2+ pets on the booth portrait): the preview session
    // belongs to a SIBLING product, and `_previewProduct.variant` still holds
    // the option values resolved at the last preview open. Reading them after
    // a swatch tap would compare the new selection against its own stale copy
    // and report "unchanged" for a change that is real. Refuse to answer
    // instead, which sends the caller to the invalidate-on-any-change default.
    if (preview._previewProduct) return null;
    // Built by the preview module, never re-implemented here: variant_key is
    // the join key into Supabase and a second slugify would be free to drift
    // from it silently.
    const key = preview.buildVariantKey();
    return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;
  }

  /** Booth product with no usable approval — the state that shuts the button. */
  boothApprovalBlocked() {
    return this.boothApprovalRequired() && !this.previewApproved();
  }

  /* ── Instant Preview requirement test (frontend#239) ──────────────────────
   * Test arm: Add to Cart needs the photo, the required fields and an approved
   * preview, like a booth product. Control, and every page outside the test,
   * is today's flow. The measurement contract (exposure event, line
   * properties) is in the #239 measurement spec. */

  /**
   * Whether this page takes part. Only where the Instant Preview button is
   * rendered and showing, so the requirement can always be met; never on a
   * booth product (its own gate, in both arms) or in a popup or in-store
   * session (the station in frontend#229 sets __instameInStoreSession).
   */
  previewRequirementEligible() {
    const section = this.querySelector('[data-preview-section]');
    return this.productData?.previewRendered === true
      && !!section && !section.hidden
      && !this.boothApprovalRequired()
      && window.__instamePopupSession !== true
      && !document.documentElement.classList.contains('popup-session')
      && window.__instameInStoreSession !== true;
  }

  /** The arm this shopper holds, or undefined when none (behaves as control, stamped nowhere). */
  previewRequirementArm() {
    const arm = (window.instameExperiments || {})[PREVIEW_REQUIRED_FLAG];
    return typeof arm === 'string' ? arm : undefined;
  }

  /** Read fresh every time, never cached: the arm can land after first paint. */
  previewRequired() {
    return this.previewRequirementArm() === 'test' && this.previewRequirementEligible();
  }

  /**
   * The approval still describes what this line carries: the booth gate's
   * check. A new photo or a text edit clears it everywhere (#242); an option
   * change that alters the printed file clears it on booth and test-arm pages
   * and marks it outdated elsewhere (reviewApprovalAfterOptionChange). This,
   * not `_preview_id`, is what `_preview_approved` reports.
   */
  previewApprovalValid() {
    return this.previewApproved() && !this._approvalOutdated;
  }

  /**
   * Instant Preview itself broke on this page: the script did not load, or
   * init, generation or the save failed. The test arm then fails OPEN (the
   * add goes through, stamped `_preview_required_bypass`) so a broken preview
   * never blocks a sale; booth products keep failing closed. The latest reason
   * wins. One of script_load_failed, init_failed, generation_failed,
   * save_failed (the backend reads exactly these).
   * @param {string} reason
   */
  notePreviewFailure(reason) {
    this.previewFailure = reason;
    this.updateBoothApprovalState();
  }

  /**
   * A later init, generation or save worked, so Instant Preview is working
   * again and the requirement applies again: only a CURRENT failure waives
   * the approval. Without this, one transient failure (a 429 on generate, a
   * blip on init) waived it for the rest of the page, even across a new
   * approval and a rename. A script that failed to load cannot recover in the
   * page, and without the module nothing calls this, so that failure stays.
   */
  notePreviewRecovered() {
    if (!this.previewFailure) {
      return;
    }
    this.previewFailure = null;
    this.updateBoothApprovalState();
  }

  /** Test arm, still waiting for an approval it can get. Greys Add to Cart. */
  previewRequirementPending() {
    return this.previewRequired() && !this.previewApprovalValid() && !this.previewFailure;
  }

  /**
   * What the test arm still needs before this add, most actionable first, or
   * null when nothing does (or this is not the test arm). The photo and the
   * required fields do not depend on the preview, so a failed preview waives
   * only the approval.
   * @param {{missingPhotoZones: Element[], missingNameInputs: Element[]}} missing
   * @returns {{reason: string, zone?: Element, input?: Element} | null}
   */
  previewRequirementGap(missing) {
    if (!this.previewRequired()) {
      return null;
    }
    if (missing.missingPhotoZones.length) {
      return { reason: 'preview_required_photo_missing', zone: missing.missingPhotoZones[0] };
    }
    const input = missing.missingNameInputs[0];
    if (input) {
      const isName = /^pet name/i.test(input.dataset.lineItemProperty || '');
      return { reason: isName ? 'preview_required_name_missing' : 'preview_required_field_missing', input };
    }
    if (this.previewRequirementPending()) {
      return { reason: 'preview_required_not_approved' };
    }
    return null;
  }

  /** Say what the gap is, where it is (the #237 messages and scrolls). */
  explainPreviewRequirement(gap) {
    if (gap.zone || gap.input) {
      if (gap.zone) {
        this.showUploadError(gap.zone, "Upload a photo to continue.");
      }
      // Wiggles the photo box, or names the empty field under it, and brings
      // it into view clear of the header and the fixed bar.
      this.scrollToFirstMissing();
      return;
    }
    this.showCartError(
      this._boothApprovalStale
        ? 'You changed an option, so preview again and tap "Looks Good" to approve.'
        : 'Tap Instant Preview and approve your design before adding to cart.',
    );
    this.nudgeToPreview();
  }

  /**
   * `preview_requirement_exposed`, once per product per session, on eligible
   * pages only, when the Instant Preview button is on screen (the
   * personalize step). With no arm held it waits for this page's flags (or
   * ten seconds), so `unassigned` means the flags gave no arm, not that they
   * had not arrived yet; it may be followed by exactly one exposure with the
   * arm if they resolve later.
   * Waits for posthog-js itself, which is what knows the session-entry
   * utm_source (and a queued stub event would carry none).
   */
  firePreviewRequirementExposure() {
    const ph = window.posthog;
    if (!ph || !ph.__loaded || !this.previewRequirementEligible()) {
      return;
    }
    const btn = this.querySelector('[data-preview-btn]');
    const previewVisible = !!btn && btn.getClientRects().length > 0;
    if (!previewVisible) {
      return;
    }
    const arm = this.previewRequirementArm() || 'unassigned';
    if (arm === 'unassigned' && !this._flagsSettled) {
      return;
    }
    const key = `instame_prx:${this.productData.id}`;
    let fired = this._prxFired || null;
    try {
      fired = sessionStorage.getItem(key) || fired;
    } catch (_) {}
    if (fired && (fired !== 'unassigned' || arm === 'unassigned')) {
      return;
    }
    this._prxFired = arm;
    try {
      sessionStorage.setItem(key, arm);
    } catch (_) {}

    let utmSource = null;
    try {
      const attribution = JSON.parse(sessionStorage.getItem('instame_session_attribution') || 'null');
      utmSource = attribution?.utm_source
        // event-tracking.liquid clears its snapshot after the session's first
        // event, so on most pages the entry source only survives here.
        || ph.sessionPropsManager?.getSessionProps?.()?.$session_entry_utm_source
        || null;
    } catch (_) {}
    const properties = {
      arm,
      product_id: this.getAnalyticsProductInfo().product_id,
      pet_count: this.visiblePetCount || 1,
      preview_rendered: previewVisible,
      utm_source: utmSource,
    };
    if (arm !== 'unassigned') {
      properties[`$feature/${PREVIEW_REQUIRED_FLAG}`] = arm;
    }
    // Already a registered super property; set on the event too so the
    // exposure carries it whatever the capture path.
    const inAppBrowser = ph.get_property?.('in_app_browser');
    if (inAppBrowser) {
      properties.in_app_browser = inAppBrowser;
    }
    this.trackUploadEvent('preview_requirement_exposed', properties);
  }

  /**
   * Paint the Add to Cart button and the re-approve notice, and tell the nav
   * whether there is an approval (on every product, not only booth ones).
   * Every approval change already calls this: acceptPreview,
   * revertGalleryIfNeeded, and updateUI on each step change.
   *
   * The button is left ENABLED in the DOM and marked aria-disabled instead. A
   * truly disabled button swallows its own click, and the whole point is that
   * tapping it explains what to do — a dead control at a booth just gets
   * tapped harder. `disabled` stays owned by renderVariantPrice (sold-out),
   * so the two states cannot fight over the same property.
   */
  updateBoothApprovalState() {
    // Once approved, the fixed bar gives the Preview button's row back to
    // Add to Cart; losing the approval brings it back.
    this.navEl?.classList.toggle('wizard__nav--approved', this.previewApproved());
    const btn = this.addToCartBtn;
    if (!btn) return;
    // aria-disabled carries BOTH the semantics and the styling (the CSS greys
    // on the attribute), so there is one source of truth and no way for the
    // look and the announced state to drift apart. An earlier version also
    // toggled a --needs-approval class that styled nothing; it is gone.
    //
    // Three reasons can grey it, all decided here so none can clear another's:
    // a booth product with no approval, the Instant Preview requirement test
    // arm with no approval (frontend#239), and a multi-pet count we cannot make
    // (showRemapUnavailable). None disables it; a tap explains.
    if (this.boothApprovalBlocked() || this.previewRequirementPending() || this._remapUnavailable) {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
    // No standing notice here by design — see the comment where one used to be
    // rendered in sections/product-wizard.liquid. The blocked state is told on
    // tap, not permanently displayed.
  }

  /** Send the shopper to the thing that unblocks them. */
  nudgeToPreview() {
    const btn = this.querySelector('[data-preview-btn]');
    if (!btn) return;
    // In the fixed bar the button is already on screen, and scrolling the page
    // to an element inside a fixed bar would only move the page under it.
    if (!this.navEl?.classList.contains('wizard__nav--sticky')) {
      this.revealAboveBar(btn);
    }
    btn.classList.remove('wizard-preview__btn--pulse');
    // Reflow so the animation restarts on a second tap rather than sitting
    // finished from the first.
    void btn.offsetWidth;
    btn.classList.add('wizard-preview__btn--pulse');
  }

  async addToCart() {
    if (this._addingToCart) return;

    // Click-intent event — fires before any validation so we capture every
    // attempt, including ones routed to the "send photo later" reminder.
    // `added_to_cart` (success) and `add_to_cart_failed` (error) fire
    // downstream; the events are non-overlapping.
    const clickVariant = this.findMatchingVariant();
    // Mapped per-count product id/title when the multi-pet test arm will
    // remap this add (getMappedTarget knows the id without a fetch);
    // otherwise the page product.
    const atcInfo = this.getAnalyticsProductInfo();
    this.trackUploadEvent('add_to_cart_clicked', {
      product_id: atcInfo.product_id,
      product_title: atcInfo.product_title,
      pet_count: this.getFilledPetCount(),
      variant_id: clickVariant ? clickVariant.id : null,
      step: this.currentStep,
    });

    const trackBlocked = (reason, extras) => {
      this.trackUploadEvent('add_to_cart_blocked', Object.assign({
        product_id: atcInfo.product_id,
        product_title: atcInfo.product_title,
        pet_count: this.getFilledPetCount(),
        variant_id: clickVariant ? clickVariant.id : null,
        step: this.currentStep,
        reason: reason,
      }, extras || {}));
    };

    // ── Non-blocking personalization (Google Merchant Center compliance) ──
    // A missing photo or required name must never hard-block Add to Cart. If a
    // photo is mid-upload we keep the existing "please wait" nudge. Otherwise,
    // the first such click this session shows a one-time reminder; any later
    // click (or "Send later" from the modal) proceeds as a deferred add with
    // the order flagged for a follow-up upload-link email. The happy path
    // (nothing missing) is unchanged.
    //
    // Multi-pet exception: an EMPTY revealed slot (2+ slots visible) cannot
    // be deferred — the shopper adds that pet's photo or removes the slot.
    // Removing is one tap, so buying the product itself is never blocked,
    // which keeps this compatible with the non-blocking rule above.
    const missing = this.getMissingPersonalization();

    if (missing.anyUploading) {
      const zone = missing.uploadingZone;
      if (zone) {
        this.showUploadError(zone, 'Photo is still uploading — please wait a moment.');
        this.wiggleZone(zone);
      }
      trackBlocked('photo_uploading');
      return;
    }

    if (this.hasEmptyVisiblePetSlot()) {
      const zone = missing.missingPhotoZones[0];
      if (zone) {
        this.showUploadError(zone, "Add this pet's photo, or remove the pet to continue.");
        // wiggleZone brings it into view clear of the header and the fixed
        // bar; a second scroll here used to race it.
        this.wiggleZone(zone);
      }
      trackBlocked('multi_pet_empty_slot');
      return;
    }

    const hasMissing = missing.missingPhotoZones.length > 0 || missing.missingNameInputs.length > 0;

    // At a popup event the item is printed on site within minutes, so there is
    // no later to send the photo to: a deferred order reaches the print station
    // with nothing to print while its owner waits at the booth. Deferring is
    // therefore blocked outright here rather than being offered and refused,
    // and the reminder modal (which promises an emailed upload link) never
    // opens. Off-site shoppers are untouched.
    //
    // EITHER signal blocks it. The session latch alone missed a booth product
    // opened outside a popup session (a direct link, a restored tab, a staff
    // device): that fell through to the reminder modal and promised an emailed
    // upload link for something pressed at a tent this afternoon. The product
    // metafield is the durable half of the condition — a booth product is made
    // at the event however its page was reached.
    if (hasMissing && (window.__instamePopupSession === true || this.boothApprovalRequired())) {
      const zone = missing.missingPhotoZones[0];
      if (zone) {
        this.showUploadError(zone, "We print this at the event, so we need the photo now.");
      }
      this.scrollToFirstMissing();
      trackBlocked('popup_personalization_required');
      return;
    }

    // ── Instant Preview requirement test arm (frontend#239) ──
    // Before the reminder modal on purpose: the test arm is the arm with no
    // "send it later", so a missing photo or field is asked for right here,
    // and an unapproved design is sent to Instant Preview.
    const requirementGap = this.previewRequirementGap(missing);
    if (requirementGap) {
      this.explainPreviewRequirement(requirementGap);
      trackBlocked(requirementGap.reason);
      return;
    }

    if (hasMissing && !this.deferReminderShown()) {
      this.showDeferModal();
      trackBlocked('personalization_missing_reminder');
      return;
    }

    // ── Booth: the design must be approved ──
    // Placed after the photo/name checks on purpose: Instant Preview needs a
    // photo (and a name, where the design requires one) before it can open, so
    // "we need the photo now" is the actionable message while one is missing.
    // Only once the shopper CAN preview does it help to tell them to.
    if (this.boothApprovalBlocked()) {
      this.showCartError(
        this._boothApprovalStale
          ? 'You changed an option, so preview again and tap "Looks Good" to approve.'
          : 'Tap Instant Preview and approve your design before adding to cart.',
      );
      this.nudgeToPreview();
      trackBlocked('booth_preview_not_approved', { was_stale: !!this._boothApprovalStale });
      return;
    }

    // When not deferring, validate the step as before (this also covers
    // variant-only products, whose option radios live on the current step).
    // When deferring we intentionally skip it — the missing photo/name is the
    // whole point.
    if (!hasMissing && !this.validateCurrentStep()) {
      trackBlocked('step_validation_failed');
      return;
    }

    if (!clickVariant) {
      this.showCartError('Please select all options.');
      trackBlocked('no_matching_variant');
      return;
    }

    // Multi-pet test arm: 2+ revealed slots buy the matching per-count
    // product. The empty-slot gate above guarantees every revealed slot is
    // filled by this point, so the revealed count that priced the page is
    // also the pet count that ships. Skipped entirely (no await) when no
    // remap applies, so the control arm keeps today's synchronous path. On
    // any mapping failure the page product's variant ships instead (tracked
    // inside resolveMappedForCart).
    let mapped = null;
    if (this.getMappedTarget()) {
      if (this._resolvingCartMap) return; // debounce double-clicks across the await
      this._resolvingCartMap = true;
      try {
        mapped = await this.resolveMappedForCart();
      } finally {
        this._resolvingCartMap = false;
      }
    }

    if (mapped === REMAP_UNRESOLVED) {
      // Blocked, not substituted — see resolveMappedForCart()'s reversal note.
      // Falling through with clickVariant is what used to add the page product
      // carrying the extra pets' assets. The notice in the nav says it once;
      // a cart error below it would say it twice.
      this.showRemapUnavailable();
      return;
    }

    return this.submitCart(mapped ? mapped.variant : clickVariant, { deferred: hasMissing, mapped: mapped });
  }

  /**
   * Build the cart request and POST it. Shared by the normal and deferred
   * (send-photo-later) paths — `opts.deferred` only affects placeholder
   * stamping, so with no deferred fields the output is identical to before.
   * @param {object} variant - the variant to add; the MAPPED per-count
   *   product's variant when opts.mapped is set (multi-pet test arm).
   * @param {{ deferred?: boolean, mapped?: { product: object, variant: object } | null }} [opts]
   */
  async submitCart(variant, opts) {
    if (this._addingToCart) return;
    // Second door. addToCart() carries the message and the nudge, but the defer
    // modal's "Send later" button calls submitCart directly — booth deferrals
    // are refused upstream so it should be unreachable, and a gate that only
    // guards the door people normally use is not a gate. Silent by design: any
    // path that lands here has already shown the shopper something.
    if (this.boothApprovalBlocked()) {
      this.trackUploadEvent('add_to_cart_blocked', {
        product_id: this.productData.id,
        reason: 'booth_preview_not_approved_submit',
      });
      return;
    }
    // Same second door for the Instant Preview requirement test arm: "Send
    // later" and the reminder's missing-markup fallback both land here, and
    // must not add a line the arm would have refused. Not silent, because an
    // arm that lands after the reminder opened would otherwise leave that tap
    // unexplained.
    const requirementGap = this.previewRequirementGap(this.getMissingPersonalization());
    if (requirementGap) {
      this.explainPreviewRequirement(requirementGap);
      this.trackUploadEvent('add_to_cart_blocked', {
        product_id: this.productData.id,
        reason: `${requirementGap.reason}_submit`,
      });
      return;
    }
    const deferred = !!(opts && opts.deferred);
    // Multi-pet remap metadata: analytics must attribute the add to the
    // product actually put in the cart, not the page the shopper stood on.
    const mapped = (opts && opts.mapped) || null;
    const analyticsProduct = mapped ? mapped.product : this.productData;
    const petCount = this.getFilledPetCount();
    // Recompute at submit time so placeholders + the deferred-fields flag match
    // exactly what's on the page now.
    const missingNow = deferred
      ? this.getMissingPersonalization()
      : { missingPhotoZones: [], missingNameInputs: [] };
    const deferredNameSet = new Set(missingNow.missingNameInputs);

    this._addingToCart = true;
    const btn = this.addToCartBtn;
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.innerHTML = '<span class="wizard-nav__spinner"></span> Adding to Cart...';

    try {
      // Persist any edited pet names onto their user_uploads rows before we commit to cart.
      await this.saveAllUploadNames();

      // Build Shopify cart request
      const formData = new FormData();
      formData.append('id', variant.id);
      formData.append('quantity', '1');

      // Internal ID for linking upsell products back to this item
      const itemId = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      formData.append('properties[_id]', itemId);

      // Visible Order ID for customer reference
      const orderId = String(Math.floor(10000000 + Math.random() * 90000000));
      formData.append('properties[Order ID]', orderId);

      // Text properties. On the deferred path, a required field the customer
      // skipped gets a clear "(to be provided)" placeholder instead of blank.
      // Hidden (unrevealed) multi-pet slots must not stamp properties at all
      // — a stray "Pet Name 3" on a 2-pet order would corrupt fulfillment.
      this.querySelectorAll('[data-line-item-property]').forEach(input => {
        if (this.isSlotHidden(input)) return;
        const label = input.dataset.lineItemProperty;
        let value = input.value?.trim() || '';
        if (!value && deferredNameSet.has(input)) value = '(to be provided)';
        formData.append(`properties[${label}]`, value);
      });

      // Photo paths — already uploaded and stored in per-slot state
      const inStoreOrder = Boolean(this.closest('#instame-in-store-ipad'));
      const hidePhotoPaths = inStoreOrder || Boolean(window.HillsdaleCustomer);
      for (const [label, state] of Object.entries(this.uploads)) {
        if (state?.photo_path) {
          // Keep the fulfillment path in the order without showing a raw
          // storage key to a customer at Shopify checkout.
          formData.append(`properties[${hidePhotoPaths ? '_' : ''}${label}]`, state.photo_path);
        }
      }

      // Deferred (send-photo-later) stamping. Skipped entirely on the happy path.
      if (deferred) {
        // Customer-visible placeholder for each photo slot with no upload.
        missingNow.missingPhotoZones.forEach(zone => {
          const label = zone.dataset.fieldLabel || 'Pet Photo';
          formData.append(`properties[${label}]`, '📮 Photo coming later: we\'ll email you, then send a preview');
        });
        // Machine flags: the backend tags these orders art-needs-photo (backend#135).
        formData.append('properties[_photo_deferred]', 'true');
        const deferredLabels = [
          ...missingNow.missingPhotoZones.map(z => z.dataset.fieldLabel || 'Pet Photo'),
          ...missingNow.missingNameInputs.map(i => i.dataset.lineItemProperty),
        ];
        formData.append('properties[_deferred_fields]', deferredLabels.join(', '));
        // No uploaded photo at all → explicit NOT-UPLOADED fulfillment refs so
        // the team never sees a blank. When a partial (multi-pet) upload exists,
        // the fulfillment-assets block below stamps the real refs instead, so we
        // leave those untouched here to avoid a duplicate property.
        if (!Object.values(this.uploads).some(u => u?.upload_id)) {
          const NOT_UPLOADED = '⚠ NOT UPLOADED — customer chose send-later; email them for the photo';
          formData.append('properties[_upload_id]', NOT_UPLOADED);
          formData.append('properties[_upload_link]', NOT_UPLOADED);
        }
      }

      // Fulfillment asset references (hidden _-prefixed props; admin-only, never
      // shown to the customer). Three id+link pairs — uploaded photo, AI image,
      // mockup — pulled fresh from the backend keyed by the exact upload the
      // customer selected, so they match what was actually ordered rather than
      // fragile in-memory state. Best-effort with a short timeout: ANY failure
      // (backend down, slow, missing data) must never block checkout — we stamp
      // placeholders and proceed. The upload is always present; AI/mockup carry
      // a "not generated / not confirmed" callout when the customer skipped or
      // didn't confirm instant preview.
      try {
        // ANY line carrying 2+ filled photo slots: ONE batch call resolves
        // every pet's refs. Keyed on the DATA, not on multiPetActive — a
        // two-pet product outside MULTI_PET_PRODUCTS (or a control-arm line)
        // otherwise landed exactly one pet's clickable refs on the order.
        // LITERAL slot 1 (the number on its field label) keeps today's
        // unsuffixed property names; slot n>1 stamps _upload_id_n /
        // _upload_link_n / _ai_job_id_n / _ai_image_link_n — matching the
        // customer-visible positional "Pet Name N" props even when a middle
        // slot is an empty/deferred gap. Same 2.5s abort budget and
        // never-block-checkout posture as the single-pet path below.
        const filledUploads = this.getOrderedFilledUploads();
        if (filledUploads.length >= 2 && window.instameUserId) {
          const params = new URLSearchParams({
            user_id: window.instameUserId,
            // The preview/AI jobs are keyed to the product actually bought —
            // the mapped per-count product when the remap kicked in.
            product_id: String(mapped ? mapped.product.id : this.productData.id),
            user_upload_ids: filledUploads.map(u => u.state.upload_id).join(','),
          });
          if (this.previewId) {
            params.set('preview_id', this.previewId);
          }

          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), 2500);
          let assets = null;
          try {
            const res = await fetch(`${this.getBackendUrl()}/api/fulfillment-assets?${params}`, { signal: ctrl.signal, headers: window.HillsdaleCustomer?.headers() || {} });
            if (res.ok) {
              assets = await res.json();
            } else {
              this.trackUploadEvent('fulfillment_assets_failed', {
                reason: 'http_' + res.status,
                product_id: this.productData.id,
                user_upload_id: filledUploads[0].state.upload_id,
                pet_count: filledUploads.length,
              });
            }
          } finally {
            clearTimeout(timer);
          }

          const NOT_GENERATED = '⚠ NOT GENERATED — customer did not use instant preview';
          const NOT_CONFIRMED = '⚠ NOT CONFIRMED — customer did not click "Looks Good"';
          filledUploads.forEach(({ state, slot }, i) => {
            const suffix = slot === 1 ? '' : `_${slot}`;
            // Response entries come back in request (filled) order, so the
            // positional fallback indexes by i, not slot.
            const petAssets = assets?.pets?.find(p => p.user_upload_id === state.upload_id)
              || assets?.pets?.[i] || null;
            formData.append(`properties[_upload_id${suffix}]`, petAssets?.upload?.id || state.upload_id);
            formData.append(`properties[_upload_link${suffix}]`, petAssets?.upload?.link || state.photo_url || '');
            formData.append(`properties[_ai_job_id${suffix}]`, petAssets?.ai?.job_id || NOT_GENERATED);
            formData.append(`properties[_ai_image_link${suffix}]`, petAssets?.ai?.link || NOT_GENERATED);
          });
          // Without a sent preview_id the backend falls back to the newest
          // multi-pet row anchored on slot 1's upload — after a slot-2+
          // photo/name change that row is STALE, so ignore the response
          // mockup and degrade to NOT CONFIRMED like the legacy path.
          let trustedMockup = this.previewId ? assets?.mockup : null;
          if (!this.multiPetActive && !this.previewId && !assets?.mockup) {
            // Lines that only reach the batch call because they carry 2+
            // photos (control arm / a product outside MULTI_PET_PRODUCTS) can
            // still hold a LEGACY single-pet preview row — which that
            // multi-pet-only fallback never returns, so they would lose the
            // linkage the single-pet path below used to give them. Ask that
            // path for the first filled slot's upload instead. Test-arm lines
            // are deliberately excluded: they already degraded to NOT
            // CONFIRMED here, and a single-pet mockup would misdescribe a
            // multi-pet order. Only reached once the batch call has answered,
            // so it can't compound a timeout; a miss still stamps NOT
            // CONFIRMED. No remap can be in play (getMappedTarget is null
            // outside the test arm), so the page product is the right key.
            trustedMockup = await this.fetchSinglePetMockup(filledUploads[0].state, this.productData.id);
          }
          formData.append('properties[_preview_id]', trustedMockup?.preview_id || this.previewId || NOT_CONFIRMED);
          formData.append('properties[_preview_image]', trustedMockup?.link || this.previewImageUrl || NOT_CONFIRMED);
        } else {
          // Slot order, never upload order: the unsuffixed refs describe the
          // FIRST FILLED slot in group order — normally slot 1, i.e. the
          // "Upload Pet Photo 1" key printify-fulfillment.server.ts falls back
          // to when _preview_id isn't a usable id. On a deferred line whose
          // slot 1 is empty that key holds only the send-later placeholder, so
          // the refs describe the slot the customer actually filled instead.
          // Empty list → nothing to resolve.
          const primaryUpload = filledUploads[0]?.state || null;
          if (primaryUpload?.upload_id && window.instameUserId) {
            const params = new URLSearchParams({
              user_id: window.instameUserId,
              product_id: String(this.productData.id),
              user_upload_id: primaryUpload.upload_id,
            });
            if (this.previewId) params.set('preview_id', this.previewId);
            if (primaryUpload.pet_name) params.set('pet_name', primaryUpload.pet_name);

            const ctrl = new AbortController();
            const timer = setTimeout(() => ctrl.abort(), 2500);
            let assets = null;
            try {
              const res = await fetch(`${this.getBackendUrl()}/api/fulfillment-assets?${params}`, { signal: ctrl.signal, headers: window.HillsdaleCustomer?.headers() || {} });
              if (res.ok) {
                assets = await res.json();
              } else {
                // Non-2xx — proceed with placeholders, but record it so PostHog
                // shows how often orders ship without resolved fulfillment refs.
                this.trackUploadEvent('fulfillment_assets_failed', {
                  reason: 'http_' + res.status,
                  product_id: this.productData.id,
                  user_upload_id: primaryUpload.upload_id,
                });
              }
            } finally {
              clearTimeout(timer);
            }

            const NOT_GENERATED = '⚠ NOT GENERATED — customer did not use instant preview';
            const NOT_CONFIRMED = '⚠ NOT CONFIRMED — customer did not click "Looks Good"';
            formData.append('properties[_upload_id]', assets?.upload?.id || primaryUpload.upload_id);
            formData.append('properties[_upload_link]', assets?.upload?.link || primaryUpload.photo_url || '');
            formData.append('properties[_ai_job_id]', assets?.ai?.job_id || NOT_GENERATED);
            formData.append('properties[_ai_image_link]', assets?.ai?.link || NOT_GENERATED);
            formData.append('properties[_preview_id]', assets?.mockup?.preview_id || this.previewId || NOT_CONFIRMED);
            formData.append('properties[_preview_image]', assets?.mockup?.link || this.previewImageUrl || NOT_CONFIRMED);
          }
        }
      } catch (err) {
        // Backend unreachable or timed out (AbortError). Never block checkout —
        // record it for PostHog, then fall back to whatever in-memory state we
        // have so the order still carries something.
        this.trackUploadEvent('fulfillment_assets_failed', {
          reason: (err && err.name === 'AbortError') ? 'timeout' : 'exception',
          message: (err && err.message) || String(err),
          product_id: this.productData?.id,
        });
        // Counter drafts still require the selected photo identity when the
        // optional fulfillment lookup times out. Keep every filled slot bound
        // to its upload instead of preserving only the preview reference.
        this.getOrderedFilledUploads().forEach(({state,slot}) => {
          const suffix = slot === 1 ? '' : `_${slot}`;
          formData.set(`properties[_upload_id${suffix}]`, state.upload_id);
          formData.set(`properties[_upload_link${suffix}]`, state.photo_url || '');
        });
        if (this.previewId) formData.append('properties[_preview_id]', this.previewId);
        if (this.previewImageUrl) formData.append('properties[_preview_image]', this.previewImageUrl);
      }
      // Hidden so it doesn't show in cart UI; still visible in Shopify admin
      // order details for the fulfillment team. Only set when the user actually
      // committed a preview that has a name layer.
      if (this.previewFontColor) {
        formData.append('properties[_Pet Name Font Color]', this.previewFontColor);
      }

      // Internal analytics linking — lets the orders/create webhook resolve
      // a Shopify order back to the anonymous browser user that placed it.
      // Underscore-prefixed properties are hidden from the customer-facing
      // cart UI in Shopify. Wrapped in a try/catch defensively — a missing
      // window.instameUserId or any thrown error here MUST never block the
      // add-to-cart flow.
      try {
        if (window.instameUserId) {
          formData.append('properties[_instame_user_id]', window.instameUserId);
        }
        if (window.instameSessionId) {
          formData.append('properties[_instame_session_id]', window.instameSessionId);
        }
        // A/B-test variants → underscore-prefixed line item properties so
        // they're hidden from the customer-facing cart UI but still flow
        // to the orders/create webhook (which strips the leading _ and
        // stamps them on the purchase event). One property per active
        // experiment.
        var experiments = window.instameExperiments || {};
        Object.keys(experiments).forEach(function (k) {
          formData.append('properties[_' + k + ']', experiments[k]);
        });
        // Instant Preview requirement test (frontend#239): whether this line's
        // design approval is still valid, as the STRING "1" or "0", on lines
        // from products in the test (previewRequirementEligible), in both arms
        // (LolalyLuo/instame-shop-backend#138 reads exactly this; anything else
        // counts as absent). A product that cannot be approved (no Preview
        // button, a booth product) gets nothing rather than a "0" that would
        // drag down the approval readout; its line still carries the arm,
        // which is per person. Never inferred from _preview_id, which can point
        // at an older preview after an invalidation. With no arm held,
        // nothing: until the flag exists this stamps no line. Never
        // _exp_-prefixed, or the order webhook would read it as an experiment.
        if (this.previewRequirementArm() && this.previewRequirementEligible()) {
          const approved = this.previewApprovalValid();
          formData.append('properties[_preview_approved]', approved ? '1' : '0');
          // Test-arm line added without approval because the preview itself
          // broke (fail open): which part broke.
          if (!approved && this.previewRequired() && this.previewFailure) {
            formData.append('properties[_preview_required_bypass]', this.previewFailure);
          }
        }
      } catch (_) {}

      const response = await fetch('/cart/add.js', { method: 'POST', body: formData });
      const result = await response.json();

      if (result.status) {
        this.showCartError(result.description || result.message || 'Error adding to cart');
        this.trackUploadEvent('add_to_cart_failed', {
          product_id: analyticsProduct.id,
          product_title: analyticsProduct.title || null,
          pet_count: petCount,
          variant_id: variant.id,
          status: result.status,
          message: result.description || result.message || null,
          source: 'shopify_rejected',
        });
        btn.disabled = false;
        btn.textContent = originalText;
        this._addingToCart = false;
        return;
      }

      btn.textContent = 'Added!';
      btn.classList.add('wizard-nav__add-to-cart--success');

      // Restore the button once the confirmation has registered. Without
      // this it stayed dead until a reload — masked on upsell-eligible
      // products by the bundle redirect, but exposed on the drawer path
      // (mugs), where a shopper who closes the drawer could never add
      // another item.
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalText;
        btn.classList.remove('wizard-nav__add-to-cart--success');
        this._addingToCart = false;
      }, 2000);

      // Deferred add succeeded — record which fields the customer skipped so we
      // can measure adoption of "send photo later".
      if (deferred) {
        this.trackUploadEvent('add_to_cart_photo_deferred', {
          product_id: this.productData.id,
          variant_id: variant.id,
          deferred_photos: missingNow.missingPhotoZones.map(z => z.dataset.fieldLabel || 'Pet Photo'),
          deferred_names: missingNow.missingNameInputs.map(i => i.dataset.lineItemProperty),
        });
      }

      // Track added_to_cart in Supabase. When the multi-pet remap kicked in,
      // product/options come from the MAPPED product so the event matches the
      // cart line actually created.
      try {
        var petNameEl = this.querySelector('[data-line-item-property="Pet Name"]')
          || this.querySelector('[data-line-item-property="Pet Name 1"]');
        if (window.instameTrack) {
          window.instameTrack('added_to_cart', {
            product_id: analyticsProduct.id,
            variant_id: variant.id,
            metadata: {
              pet_name: petNameEl?.value?.trim() || null,
              price: variant.price,
              quantity: 1,
              pet_count: petCount,
              product_title: analyticsProduct.title || null,
              variant_title: variant.title || null,
              options: this.getVariantOptions(variant, analyticsProduct)
            }
          });
        }
      } catch (_) {}

      // Determine if this product goes to the post-add Bundle & Save
      // interstitial or opens the cart drawer. Permanent for upsell-eligible
      // products: the exp_skip_bundle_upsell readout ended in control's
      // favor (every directional signal — checkout rate, purchases, bundle
      // attach — favored keeping the page), so the skip arm was retired.
      // Popup-event sessions (see the flag in product-wizard.liquid) always
      // skip it: the interstitial recommends ship-to-home items, and adding
      // one to a booth pickup cart silently removes the Local Pickup option
      // at checkout.
      const productUrl = window.location.pathname.toLowerCase();
      const isUpsellEligible =
        (productUrl.includes('portrait') || productUrl.includes('blanket') || productUrl.includes('weekender')) &&
        window.__instamePopupSession !== true;

      if (window.__instameInStoreSession === true) {
        // The station owns the cart screen. Do not wake the theme's cart
        // drawer or bundle interstitial alongside its own sequential cart
        // read; either can race the next Cart Ajax request on a shared iPad.
      } else if (isUpsellEligible) {
        // Prevent cart drawer from opening — user is about to be redirected to bundle page
        const drawer = document.querySelector('cart-drawer-component');
        const hadAutoOpen = drawer?.hasAttribute('auto-open');
        if (hadAutoOpen) drawer.removeAttribute('auto-open');

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'product-form-component', itemCount: 1 } },
        }));

        // Restore auto-open in case redirect is cancelled
        if (hadAutoOpen) requestAnimationFrame(() => drawer.setAttribute('auto-open', ''));

        const productHandle = productUrl.replace('/products/', '').split('?')[0];
        setTimeout(() => {
          window.location.href = '/pages/bundle-and-save?from=' + encodeURIComponent(productHandle);
        }, 800);
      } else {
        // Non-upsell product (e.g. mugs): update cart count and let the drawer open naturally
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data: { source: 'product-form-component', itemCount: 1 } },
        }));
      }
      return result;
    } catch (error) {
      console.error('Add to cart error:', error);
      this.trackUploadEvent('add_to_cart_failed', {
        product_id: analyticsProduct.id,
        product_title: analyticsProduct.title || null,
        pet_count: petCount,
        variant_id: variant.id,
        message: error && (error.message || String(error)),
        source: 'network_error',
      });
      this.showCartError(error.message || 'Something went wrong. Please try again.');
      btn.disabled = false;
      btn.textContent = originalText;
      this._addingToCart = false;
    }
  }

  /** The confirmed mockup for ONE upload, via the single-pet shape of
   *  /api/fulfillment-assets (which resolves single-pet preview rows the
   *  multi-pet batch fallback filters out). Best-effort on the add-to-cart
   *  path: a non-2xx, a timeout or an unreachable backend all resolve null so
   *  the caller stamps its NOT-CONFIRMED placeholder — this never throws and
   *  never blocks checkout.
   *  @param {{ upload_id: string, pet_name?: string }} state the slot's upload
   *  @param {string|number} productId the product the preview was saved under
   *  @returns {Promise<{ preview_id: string, link: string|null }|null>} */
  async fetchSinglePetMockup(state, productId) {
    const params = new URLSearchParams({
      user_id: window.instameUserId,
      product_id: String(productId),
      user_upload_id: state.upload_id,
    });
    if (state.pet_name) {
      params.set('pet_name', state.pet_name);
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 2500);
    try {
      const res = await fetch(`${this.getBackendUrl()}/api/fulfillment-assets?${params}`, { signal: ctrl.signal, headers: window.HillsdaleCustomer?.headers() || {} });
      if (!res.ok) {
        return null;
      }
      const assets = await res.json();
      return assets?.mockup || null;
    } catch {
      // Aborted, offline or unparseable — same outcome as no confirmed row.
      return null;
    } finally {
      clearTimeout(timer);
    }
  }

  initInstantPreview() {
    // Font loading is handled inside instant-preview.js (see InstantPreview.ensureFontLoaded
    // and FONT_URLS) — driven by templateConfig.default_name_config from Supabase.

    // The button now ships clickable (aria-disabled, not disabled) so that a tap
    // while something is missing explains itself. The handler that does the
    // explaining lives in instant-preview.js, which is injected just below and
    // therefore binds asynchronously: a tap in that window would hit a button
    // with no listener at all, the exact dead control this change removes, only
    // narrower. This covers that window and stands down the moment the module is
    // live. The hint text restores on a timer, so a slow load cannot leave the
    // line permanently reading "still loading". If the module never arrives
    // (script.onerror below) it keeps answering, with the failure instead.
    this.querySelector('[data-preview-btn]')?.addEventListener('click', () => {
      if (this.instantPreview) return; // the module's own handler owns the click
      this.flashPreviewHint(this._previewUnavailableMessage || 'Preview is still loading, one moment.');
    });

    // Delegated, so it also reaches a custom-text field whose name only the
    // preview's template config knows. Input from a field the design does not
    // show is a no-op: reviewApprovalAfterTextEdit compares the drawn text only.
    this.addEventListener('input', () => this.reviewApprovalAfterTextEdit());

    // The station opens multiple products without a page reload. Reuse the
    // already loaded preview class instead of injecting its top-level consts
    // a second time, which would throw before the new wizard can bind.
    if (window.InstantPreview) {
      this.instantPreview = new window.InstantPreview(this);
      return;
    }

    // Load and init the preview module
    const script = document.createElement('script');
    script.src = this.assetUrl('instant-preview.js');
    script.onload = () => {
      if (window.InstantPreview) {
        this.instantPreview = new window.InstantPreview(this);
      }
    };
    script.onerror = () => {
      // Without this, a failed asset load (CDN hiccup, aggressive extension,
      // flaky mobile network) left the Preview button permanently dead with
      // no signal anywhere. The deferred-photo add-to-cart path works
      // without the preview module, so degrade visibly instead of blocking.
      this.trackUploadEvent('instant_preview_script_load_failed', {
        src: script.src,
      });
      // The requirement test arm fails open on this (booth stays closed).
      this.notePreviewFailure('script_load_failed');
      // On a booth product "you can still add to cart" is now false, and it
      // would be a worse lie than a dead button: without the preview module
      // there is no approved design and therefore no artwork for the press.
      // Blocking is the honest failure — staff can see it immediately and
      // pull the product, rather than a customer paying for a job that
      // reaches the press empty.
      this._previewUnavailableMessage = this.boothApprovalRequired()
        ? 'Preview is unavailable right now. Please ask a member of staff.'
        : 'Preview is unavailable right now. You can still add to cart.';
      // Greyed, not disabled, and the reason is on screen rather than in a
      // title tooltip, which never shows on a phone. The hint line carries it
      // as its standing text (the "upload a photo" advice would now be
      // wrong), and a tap on the button repeats it, in the fixed bar too.
      const btn = this.querySelector('[data-preview-btn]');
      btn?.setAttribute('aria-disabled', 'true');
      const hint = this.querySelector('[data-preview-hint]');
      if (hint) {
        hint.dataset.defaultText = this._previewUnavailableMessage;
        hint.textContent = this._previewUnavailableMessage;
        hint.hidden = false;
      }
      if (this.boothApprovalRequired()) this.updateBoothApprovalState();
    };
    document.head.appendChild(script);
  }

  assetUrl(filename) {
    const attr = `data-${filename.replace(/\.[^.]+$/, "")}-url`;
    const url = this.getAttribute(attr);
    if (url) return url;
    // Fallback: derive from product-wizard.js script tag (same CDN host/path)
    const existingScript = document.querySelector('script[src*="product-wizard"]');
    if (existingScript) {
      return existingScript.src.replace('product-wizard.js', filename);
    }
    return `/cdn/shop/t/1/assets/${filename}`;
  }

  showCartError(message) {
    const errorEl = this.querySelector('[data-cart-error]');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      // 6s, not 4s. These are full sentences with an instruction in them
      // ('Tap Instant Preview and approve your design before adding to cart'),
      // and they are read at a booth with a queue behind you. Four seconds was
      // not long enough to read one and act on it.
      //
      // The pending timer is CANCELLED first, or a repeat tap inherits the
      // previous one's remaining time: tap, wait 5s, tap again, and the second
      // message is wiped after 1s instead of 6. Lengthening the timeout widened
      // that window rather than creating it, and a booth shopper who taps a
      // blocked button twice is exactly who reads the message least carefully.
      clearTimeout(this._cartErrorTimer);
      this._cartErrorTimer = setTimeout(() => { errorEl.hidden = true; }, 6000);
    }
  }

}

if (!customElements.get('product-wizard')) {
  customElements.define('product-wizard', ProductWizard);
}
